import React, { useCallback, useEffect, useMemo, useState } from 'react';
import logo from './images/drsatyaoverseaslogo.png';
import frontimage from './images/fronthomeimage.jpeg';
import groupimage from './images/groupimage.jpeg';
import ogImage from './images/og-image.jpg';
import twitterImage from './images/twitter-image.jpg';
import qrCode from './images/websiteqrcode.png';
import canThoCollege from './images/canthocollege.jpg';
import canThoLogo from './images/canthologo.png';
import pctuCollege from './images/pctucollege.jpg';
import pctuLogo from './images/pctulogo.png';
import daiNamCollege from './images/dainamcollege.jpg';
import daiNamLogo from './images/dainamlogo.png';
import bmtuCollege from './images/bmtucollege.png';
import bmtuLogo from './images/BMTUlogo.webp';
import dongACollege from './images/dongacollege.jpg';
import dongALogo from './images/dongalogo.png';
import namCanThoCollege from './images/namcanthocollege.jpg';
import namCanThoLogo from './images/namcanthologo.png';
import aryanSingh from './images/reviewusers/aryan_singh.jpeg';
import chiragGupta from './images/reviewusers/Chirag_Gupta.jpg';
import narayanShukla from './images/reviewusers/NarayanShukla.jpg.jpeg';
import pankhuriShukla from './images/reviewusers/Pankhuri_Shukla.jpg';
import rituKushwaha from './images/reviewusers/Ritu_kushwaha.jpg';
import shauryaVerma from './images/reviewusers/Shaurya_Verma.jpeg';
import nitinMishra from './images/reviewusers/nitinmishralucknow.jpeg';
import soumyaMishra from './images/reviewusers/soumyamishralucknow.jpeg';
import './App.css';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (typeof window !== 'undefined' && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return 'https://backend.drsatyaoverseas.com';
  }

  return 'http://localhost:5001';
};

const API_BASE_URL = getApiBaseUrl();
const BROCHURE_PATH = '/dr-satya-overseas-brochure.pdf';
const GA_MEASUREMENT_ID = 'G-GS79PWJK3S';
const API_ROUTE_NOT_FOUND_MESSAGE = 'Backend API route is not available on the live server. Please deploy/restart the latest server code on backend.drsatyaoverseas.com.';

const readApiResponse = async (response) => {
  const text = await response.text();
  const contentType = response.headers.get('content-type') || '';
  let data = {};

  if (text) {
    if (contentType.includes('application/json')) {
      try {
        data = JSON.parse(text);
      } catch (error) {
        throw new Error('Backend returned invalid JSON. Please check the live server logs.');
      }
    } else if (text.includes('<!DOCTYPE') || text.includes('<html') || text.includes('Cannot POST') || text.includes('Cannot GET')) {
      if (!response.ok) {
        throw new Error(response.status === 404 ? API_ROUTE_NOT_FOUND_MESSAGE : 'Backend returned an HTML error page. Please check the live server route.');
      }
    } else {
      data = { message: text };
    }
  }

  if (!response.ok) {
    throw new Error(data.message || data.errors?.[0]?.msg || `Request failed with status ${response.status}`);
  }

  return data;
};

const DEFAULT_FILTERS = {
  q: '',
  status: 'all',
  priority: 'all',
  source: 'all',
  followUp: 'all',
  sort: 'newest',
  dateFrom: '',
  dateTo: '',
};

const STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  'follow-up': 'Follow-up',
  qualified: 'Qualified',
  converted: 'Converted',
  'not-interested': 'Not interested',
  closed: 'Closed',
};

const PRIORITY_LABELS = {
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold',
};

const WHY_VIETNAM = [
  {
    icon: 'wallet',
    title: 'Affordable and planned education',
    text: 'Vietnam gives students a structured MBBS pathway with counselling around tuition, hostel, living budget, and intake timelines before they apply.',
  },
  {
    icon: 'users',
    title: 'Growing student ecosystem',
    text: 'Indian students get practical support for documentation, travel, arrival, accommodation, food options, and parent communication.',
  },
  {
    icon: 'book',
    title: 'English-medium guidance',
    text: 'Program language, recognition, and eligibility differ by institution and intake. Dr Satya Overseas verifies these details before shortlisting.',
  },
];

const ADMISSION_STEPS = [
  {
    icon: 'profile',
    title: 'Profile review',
    text: 'NEET, PCB marks, passport status, budget, and preferred intake are checked first.',
  },
  {
    icon: 'shortlist',
    title: 'College shortlist',
    text: 'Students receive suitable Vietnam university options with fee, hostel, and timeline clarity.',
  },
  {
    icon: 'file',
    title: 'Application and offer',
    text: 'Documents are prepared, application is submitted, and offer letter progress is tracked.',
  },
  {
    icon: 'plane',
    title: 'Visa and departure',
    text: 'Visa file, travel planning, forex, packing, and arrival coordination are handled with the student.',
  },
];

const REQUIRED_DOCUMENTS = [
  {
    title: '10th marksheet',
    text: 'Clear scan or photo of the student 10th marksheet.',
  },
  {
    title: 'High school certificate',
    text: 'Readable high school certificate copy for academic verification.',
  },
  {
    title: '12th marksheet',
    text: 'Clear scan or photo of the student 12th marksheet.',
  },
  {
    title: 'Transfer Certificate (TC)',
    text: 'Transfer certificate issued by the last attended school.',
  },
  {
    title: 'Student passport (3 pages)',
    text: 'Passport copies required for admission and visa file preparation.',
  },
  {
    title: 'Student birth certificate',
    text: 'Birth certificate details should match academic documents and passport.',
  },
  {
    title: 'Student Aadhar card',
    text: 'Student identity proof for documentation and record matching.',
  },
  {
    title: 'Student PAN card',
    text: 'Student PAN card copy for file completion where required.',
  },
  {
    title: 'Student passport size photo',
    text: 'Recent passport size photograph with a clean background.',
  },
  {
    title: 'Father Aadhar card',
    text: 'Father identity proof copy for student admission records.',
  },
  {
    title: 'Mother Aadhar card',
    text: 'Mother identity proof copy for student admission records.',
  },
  {
    title: 'Father PAN card',
    text: 'Father PAN card copy for financial and sponsor documentation.',
  },
  {
    title: 'Mother PAN card',
    text: 'Mother PAN card copy for financial and sponsor documentation.',
  },
];

const COLLEGES = [
  {
    icon: 'college',
    logo: canThoLogo,
    image: canThoCollege,
    name: 'Can Tho University (CTUMP)',
    city: 'Can Tho',
    fee: 'Reference fee: 4 Lakhs / year',
    website: 'https://www.ctump.edu.vn/',
    text: 'A long-established government university option discussed with Indian curriculum guidance, early clinical exposure, modern labs, and student support planning.',
    points: [
      { icon: 'building', text: '43-year-old government university profile' },
      { icon: 'checklist', text: 'Current NMC status to be verified' },
      { icon: 'book', text: 'Indian curriculum discussion' },
      { icon: 'hospital', text: 'Early clinical exposure from 1st year' },
      { icon: 'medical', text: 'Modern infrastructure and labs' },
      { icon: 'target', text: 'Access to anatomy learning resources' },
      { icon: 'home', text: 'Hostel and Indian food guidance' },
      { icon: 'plane', text: 'Around 4-hour flight planning from India' },
    ],
  },
  {
    icon: 'hospital',
    logo: pctuLogo,
    image: pctuCollege,
    name: 'Phan Chau Trinh University (PCTU)',
    city: 'Da Nang',
    fee: 'Reference fee: 4.4 Lakhs / year',
    website: 'https://pctu.edu.vn/',
    text: 'A private university option discussed with clinical exposure, simulation facilities, university-owned hospitals, and US-modeled curriculum references.',
    points: [
      { icon: 'building', text: 'Top private university profile' },
      { icon: 'checklist', text: 'Current NMC status to be verified' },
      { icon: 'book', text: 'US-modeled curriculum reference' },
      { icon: 'target', text: 'NEXT and USMLE preparation orientation' },
      { icon: 'hospital', text: 'Clinical exposure from 1st year' },
      { icon: 'medical', text: 'Simulation hospital and research centre' },
      { icon: 'campus', text: 'University-owned hospitals' },
      { icon: 'check', text: 'Hands-on anatomy and practical exposure' },
    ],
  },
  {
    icon: 'city',
    logo: daiNamLogo,
    image: daiNamCollege,
    name: 'Dai Nam University (DNU)',
    city: 'Hanoi',
    fee: 'Reference fee: 3.5 Lakhs / year',
    website: 'https://dainam.edu.vn/',
    text: 'A Hanoi-based private university option positioned around NEXT readiness, affiliated hospitals, and affordable planning.',
    points: [
      { icon: 'building', text: 'Top private university in Hanoi' },
      { icon: 'checklist', text: 'Current NMC status to be verified' },
      { icon: 'target', text: 'NEXT exam readiness discussion' },
      { icon: 'hospital', text: 'University-affiliated hospitals' },
      { icon: 'wallet', text: 'Affordable tuition planning' },
      { icon: 'city', text: 'Hanoi city and safety overview' },
      { icon: 'home', text: 'Hostel and Indian food discussion' },
      { icon: 'file', text: 'Travel and document guidance' },
    ],
  },
  {
    icon: 'medical',
    logo: bmtuLogo,
    image: bmtuCollege,
    name: 'Buon Ma Thuot University (BMTU)',
    city: 'Central Highlands',
    fee: 'Reference fee: 3.8 Lakhs / year',
    website: 'https://bmtu.edu.vn/',
    text: 'A medical university option mentioned with on-campus hospital exposure, outpatient volume reference, and multi-hospital affiliations.',
    points: [
      { icon: 'building', text: 'Large private medical university profile' },
      { icon: 'hospital', text: '500+ bed multi-speciality hospital campus reference' },
      { icon: 'users', text: 'Government-level outpatient exposure discussion' },
      { icon: 'campus', text: 'Government and private hospital affiliations' },
      { icon: 'india', text: 'Indian student enrolment guidance' },
      { icon: 'home', text: 'Separate hostel planning for boys and girls' },
      { icon: 'medical', text: 'Simulation lab and hands-on training' },
      { icon: 'wallet', text: 'Scholarship discussion based on academics' },
    ],
  },
  {
    icon: 'campus',
    logo: dongALogo,
    image: dongACollege,
    name: 'Dong A University (UDA)',
    city: 'Da Nang',
    fee: 'Reference fee: $4,500 / year',
    website: 'https://donga.edu.vn/',
    text: 'A Da Nang university option discussed for city comfort, NEXT readiness, and hospital partnership references.',
    points: [
      { icon: 'building', text: 'Top private university in Da Nang' },
      { icon: 'checklist', text: 'Current NMC status to be verified' },
      { icon: 'target', text: 'NEXT exam readiness discussion' },
      { icon: 'hospital', text: 'VinMec Hospital partnership reference' },
      { icon: 'city', text: 'Da Nang city comfort overview' },
      { icon: 'home', text: 'Hostel and food planning' },
      { icon: 'users', text: 'Travel and arrival assistance' },
      { icon: 'plane', text: 'Around 5-hour flight planning from India' },
    ],
  },
  {
    icon: 'college',
    logo: namCanThoLogo,
    image: namCanThoCollege,
    name: 'Nam Can Tho University (NCTU)',
    city: 'Can Tho',
    fee: 'Reference fee: verify for current intake',
    website: 'https://nctu.edu.vn/',
    text: 'A Can Tho private university option added for counselling shortlists, with focus on campus facilities, student services, and current intake verification.',
    points: [
      { icon: 'building', text: 'Private university in Can Tho city' },
      { icon: 'checklist', text: 'Current NMC and eligibility status to be verified' },
      { icon: 'book', text: 'Program medium and curriculum confirmation' },
      { icon: 'medical', text: 'Medical faculty and practical learning discussion' },
      { icon: 'home', text: 'Hostel, food, and daily budget planning' },
      { icon: 'city', text: 'Can Tho city comfort and safety overview' },
      { icon: 'file', text: 'Admission documents and intake guidance' },
      { icon: 'phone', text: 'Parent coordination and student support' },
    ],
  },
];

const LIFE_IN_VIETNAM = [
  {
    icon: 'college',
    title: 'College life',
    text: 'Students adjust to classroom learning, lab work, hospital visits, group study, and university events. The first few weeks are focused on settling in.',
  },
  {
    icon: 'home',
    title: 'Hostel and food',
    text: 'Accommodation, mess or self-cooking options, nearby Indian food availability, and daily budget are discussed before departure.',
  },
  {
    icon: 'city',
    title: 'City life',
    text: 'Vietnam offers clean cities, public transport, cafes, student-friendly areas, and a calm lifestyle for focused medical education.',
  },
  {
    icon: 'heart',
    title: 'Parent support',
    text: 'Families need reliable updates. Dr Satya Overseas keeps the process clear from admission stage to arrival and early campus adjustment.',
  },
];

const AFTER_MBBS = [
  {
    icon: 'india',
    title: 'Return to India planning',
    text: 'Students should understand the latest Indian licensing pathway, internship requirements, and exam preparation expectations before choosing a college.',
  },
  {
    icon: 'target',
    title: 'FMGE or NExT preparation mindset',
    text: 'A serious study plan starts from the first year. Counselling explains why clinical discipline and regular revision matter.',
  },
  {
    icon: 'globe',
    title: 'PG and global options',
    text: 'Career direction after MBBS depends on regulations, exam performance, internship, language, and country-specific licensing rules.',
  },
  {
    icon: 'compass',
    title: 'Realistic career guidance',
    text: 'No shortcut promises. Students get a practical view of hard work, timelines, documentation, and licensing responsibilities.',
  },
];

const PHOTO_STACKS = [
  {
    slug: 'life-in-vietnam',
    cloudinaryFolders: ['lifeinveitnam', 'lifeinvietnam', 'life-in-vietnam'],
    icon: 'globe',
    title: 'Life in Vietnam',
    text: 'City comfort, travel, food, safety, local culture, and student routines.',
    cover: frontimage,
    photos: [
      { src: frontimage, caption: 'Vietnam campus environment and student arrival support' },
      { src: groupimage, caption: 'Indian student community and counselling support' },
      { src: ogImage, caption: 'University events and guided student interactions' },
    ],
  },
  {
    slug: 'life-in-college',
    cloudinaryFolders: ['lifeincollege', 'life-in-college'],
    icon: 'college',
    title: 'Life in College',
    text: 'Classes, peer learning, campus events, mentoring, and academic discipline.',
    cover: groupimage,
    photos: [
      { src: groupimage, caption: 'Student group and campus interaction' },
      { src: ogImage, caption: 'College ceremony and academic environment' },
      { src: twitterImage, caption: 'Student team learning and university activities' },
    ],
  },
  {
    slug: 'machines-and-labs',
    cloudinaryFolders: ['machineandlabs', 'machinesandlabs', 'machines-and-labs'],
    icon: 'medical',
    title: 'Machines and Labs',
    text: 'Simulation, anatomy, clinical equipment, lab exposure, and practical learning.',
    cover: ogImage,
    photos: [
      { src: ogImage, caption: 'Medical university event and learning infrastructure' },
      { src: twitterImage, caption: 'Hands-on learning and guided medical exposure' },
      { src: frontimage, caption: 'Campus facilities and modern academic setup' },
    ],
  },
  {
    slug: 'hostel-and-student-life',
    cloudinaryFolders: ['hostelandstudentlife', 'hostel-and-student-life'],
    icon: 'home',
    title: 'Hostel and Student Life',
    text: 'Accommodation, food, daily budget, Indian food options, and settling-in support.',
    cover: twitterImage,
    photos: [
      { src: twitterImage, caption: 'Student life and support environment' },
      { src: groupimage, caption: 'Peer group and community comfort' },
      { src: frontimage, caption: 'University surroundings and student movement' },
    ],
  },
  {
    slug: 'clinical-exposure',
    cloudinaryFolders: ['clinalexposure', 'clinicalexposure', 'clinical-exposure'],
    icon: 'hospital',
    title: 'Clinical Exposure',
    text: 'Hospital visits, early exposure, patient-care understanding, and career discipline.',
    cover: frontimage,
    photos: [
      { src: frontimage, caption: 'Campus and hospital exposure planning' },
      { src: ogImage, caption: 'University-level medical learning environment' },
      { src: twitterImage, caption: 'Student participation in academic activities' },
    ],
  },
  {
    slug: 'activities',
    cloudinaryFolders: ['activities'],
    icon: 'users',
    title: 'Activities',
    text: 'Student activities, events, celebrations, seminars, and guided university moments.',
    cover: groupimage,
    photos: [
      { src: groupimage, caption: 'Student activities and university events' },
      { src: ogImage, caption: 'Guided student participation and academic activities' },
      { src: twitterImage, caption: 'Student community moments in Vietnam' },
    ],
  },
];

const SERVICES = [
  { icon: 'profile', title: 'Free eligibility counselling' },
  { icon: 'shortlist', title: 'Vietnam college shortlisting' },
  { icon: 'wallet', title: 'Fee and living cost explanation' },
  { icon: 'file', title: 'Application and offer letter support' },
  { icon: 'visa', title: 'Visa file and travel guidance' },
  { icon: 'checklist', title: 'Forex, packing, and arrival checklist' },
  { icon: 'users', title: 'Parent coordination' },
  { icon: 'heart', title: 'Post-arrival student support' },
];

const REVIEWS = [
  {
    name: 'Aryan Singh',
    location: 'Lucknow',
    role: 'Student',
    photo: aryanSingh,
    text: 'Dr Satya Overseas helped me understand Vietnam MBBS options with complete clarity. The counselling was practical, transparent, and focused on eligibility, fees, and the right university fit.',
  },
  {
    name: 'Chirag Gupta',
    location: 'New Delhi',
    role: 'Student',
    photo: chiragGupta,
    text: 'The team explained the admission process step by step and gave a realistic view of colleges, documents, visa requirements, and timelines. It made the decision much easier for my family.',
  },
  {
    name: 'Narayan Shukla',
    location: 'Lucknow',
    role: 'Student',
    photo: narayanShukla,
    text: 'I received clear guidance on fee structure, hostel planning, safety, and the complete admission flow. The counselling felt responsible and professional throughout.',
  },
  {
    name: 'Pankhuri Shukla',
    location: 'Lucknow',
    role: 'Technical professional',
    photo: pankhuriShukla,
    text: 'I was coordinating my brother\'s admission and needed clear, practical answers. Dr Satya Overseas explained the university options, documents, timelines, and fee planning in a very professional way.',
  },
  {
    name: 'Ritu Kushwaha',
    location: 'New Delhi',
    role: 'Student',
    photo: rituKushwaha,
    text: 'The counsellor answered my questions about safety, accommodation, food, travel, and student support. The process was explained in a calm and trustworthy way.',
  },
  {
    name: 'Shaurya Verma',
    location: 'Lucknow',
    role: 'Student',
    photo: shauryaVerma,
    text: 'The best part was honest counselling. I got a practical understanding of Vietnam MBBS, university selection, visa support, and what preparation is needed after admission.',
  },
  {
    name: 'Nitin Mishra',
    location: 'Lucknow',
    role: 'Family coordinator',
    photo: nitinMishra,
    text: 'The counselling gave our family a clear picture of Vietnam MBBS options, fees, documents, and admission timelines. The team explained every step patiently and professionally.',
  },
  {
    name: 'Soumya Mishra',
    location: 'Lucknow',
    role: 'Student',
    photo: soumyaMishra,
    text: 'I received helpful guidance about college selection, hostel planning, safety, and the application process. The support felt practical, transparent, and easy to follow.',
  },
];

const FAQS = [
  ['Is Vietnam MBBS good for Indian students?', 'It can be a good option when the university, language medium, recognition, budget, and licensing pathway are verified for the current intake.'],
  ['Can I study MBBS abroad if I failed NEET?', 'Indian students should verify the latest NEET and NMC eligibility rules before choosing any foreign MBBS program. If NEET is not qualified, counselling can help with retake planning, next intake strategy, and legal options.'],
  ['What is the MBBS in Vietnam fee structure?', 'Fees vary by university and intake. Counselling covers tuition, hostel, food, visa, travel, insurance, and living costs so the family sees the full budget.'],
  ['Will Dr Satya Overseas help with documents?', 'Yes. The counselling flow includes document checklist, application support, visa file guidance, and departure preparation.'],
  ['Can parents speak with the counsellor?', 'Yes. Parent counselling is important because fee planning, hostel, safety, travel, and long-term career planning should be clear.'],
  ['Do you guarantee results or licensing?', 'No genuine counsellor should guarantee licensing results. Students must prepare seriously and follow the latest rules of the relevant authorities.'],
];

const SOCIAL_LINKS = [
  { icon: 'facebook', label: 'Facebook', href: 'https://www.facebook.com/drsatyaoverseas' },
  { icon: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/drsatyaoverseas' },
  { icon: 'twitter', label: 'Twitter X', href: 'https://twitter.com/drsatyaoverseas' },
  { icon: 'youtube', label: 'YouTube', href: 'https://www.youtube.com/@drsatyaoverseas' },
  { icon: 'linkedin', label: 'LinkedIn', href: 'https://www.linkedin.com/company/drsatyaoverseas' },
];

const GOOGLE_MAP_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28465.597474705468!2d80.96820831298825!3d26.8971570977439!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3999593b3cfb51dd%3A0xbbce94d2d90c2de5!2sDr.%20Satya%20Overseas%20%26%20Clinic!5e0!3m2!1sen!2sin!4v1776675032743!5m2!1sen!2sin';
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिंदी' },
  { value: 'vi', label: 'Tiếng Việt' },
];
const DEFAULT_INTEREST_VALUES = ['MBBS in Vietnam', 'वियतनाम में MBBS', 'MBBS tại Việt Nam'];

const PUBLIC_CONTENT = {
  en: {
    seo: {
      title: 'MBBS in Vietnam for Indian Students | Dr Satya Overseas',
      description: 'MBBS in Vietnam counselling for Indian students. Compare Vietnam medical universities, fees, hostel, visa, eligibility, NEET guidance, admission process, and post-MBBS career planning with Dr Satya Overseas.',
    },
    brandSub: 'Official Guidance - Vietnam MBBS',
    header: {
      languageLabel: 'Language',
      callNow: 'Call Now',
      brochure: 'Brochure',
      applyNow: 'Apply Now',
      whyVietnam: 'Why Vietnam',
      colleges: 'Colleges',
      studentLife: 'Student Life',
      reviews: 'Reviews',
    },
    form: {
      defaultInterest: 'MBBS in Vietnam',
      sourceLabels: {
        Popup: 'Popup enquiry',
        'Hero Eligibility Form': 'Hero eligibility form',
        'Free Counselling': 'Free counselling',
        'Download Brochure': 'Download brochure',
        'Apply Now': 'Apply now',
        'Contact Us': 'Contact us',
        'Trust Box CTA': 'Trust box enquiry',
        'Review CTA': 'Shortlist CTA',
        'Floating Enquiry': 'Floating enquiry',
      },
      quickEligibilityTitle: 'Quick eligibility check',
      contactTitle: 'Submit your enquiry',
      intro: 'Share your details and our Vietnam MBBS counsellor will call or WhatsApp you.',
      fields: {
        name: 'Full name',
        phone: 'Mobile number',
        email: 'Email optional',
        interest: 'Interest',
        question: 'Question optional',
      },
      placeholders: {
        name: 'Your name',
        phone: '10 to 15 digit number',
        email: 'name@example.com',
        interest: 'MBBS in Vietnam',
        question: 'Ask about fees, hostel, eligibility, visa, or colleges',
      },
      validation: {
        invalidName: 'Please enter a valid name.',
        invalidPhone: 'Please enter a valid mobile number.',
        invalidEmail: 'Please enter a valid email or leave it blank.',
      },
      submit: 'Submit enquiry',
      submitting: 'Submitting...',
      success: 'Enquiry submitted successfully. Our team will contact you shortly.',
      error: 'Unable to submit right now. Please try again in a moment.',
      privacy: 'Your details are used only for admission counselling and follow-up.',
      fallbackMessage: 'Vietnam MBBS counselling enquiry',
    },
    modal: {
      close: 'Close',
      closeAria: 'Close enquiry form',
      title: 'Get Vietnam MBBS guidance',
      text: 'Leave your number and the counselling team will call or WhatsApp you.',
    },
    hero: {
      badge: 'Direct admissions - MOU partner college guidance',
      title: 'MBBS in Vietnam for Indian students with transparent guidance.',
      description: 'Shortlist colleges, understand fee structure, compare hostel and living costs, and complete admission, visa, and departure steps with Dr Satya Overseas.',
      points: [
        ['profile', 'Eligibility and profile review', 'NEET, PCB, budget, documents, and intake fit.'],
        ['wallet', 'Fee and timeline clarity', 'Tuition, hostel, living cost, application, and visa stages.'],
        ['checklist', 'End-to-end support', 'Offer letter, visa file, travel, arrival, and parent coordination.'],
      ],
      primaryCta: 'Get free counselling',
      secondaryCta: 'Download brochure',
      trust: [
        ['phone', '24h', 'Working-day response'],
        ['checklist', 'Verified', 'College shortlisting'],
        ['wallet', 'Clear', 'Fees and process'],
        ['visa', 'Visa', 'Document guidance'],
      ],
    },
    whyVietnam: {
      eyebrow: 'Why Vietnam',
      title: 'Built for students who want clarity before choosing a country.',
      description: 'The first decision is not just the university. It is budget, eligibility, language medium, documents, hostel, safety, and licensing preparation.',
      note: 'Recognition, language medium, eligibility, fees, and intake details can change. Final counselling should verify the current university documents before admission.',
      items: WHY_VIETNAM,
    },
    process: {
      eyebrow: 'Admission process',
      title: 'From first call to campus arrival.',
      items: ADMISSION_STEPS,
    },
    colleges: {
      eyebrow: 'College details',
      title: 'Vietnam medical college options discussed during counselling.',
      description: 'The exact shortlist depends on student profile, budget, current intake, documents, and the university\'s latest rules.',
      applyNow: 'Apply Now',
      downloadBrochure: 'Brochure',
      items: COLLEGES,
    },
    life: {
      eyebrow: 'Life in Vietnam',
      title: 'Campus life, hostel, food, city comfort, and family updates matter.',
      description: 'Students are moving to another country for years. Counselling should cover everyday life with the same seriousness as admission documents.',
      items: LIFE_IN_VIETNAM,
    },
    photoStacks: {
      eyebrow: 'Student photo stories',
      title: 'Open each stack to explore Vietnam life, college, labs, hostel, and clinical exposure.',
      description: 'These photo stacks are ready for your real student photos. Each stack opens a gallery so students and parents can understand the environment visually.',
      modalEyebrow: 'Photo stack',
      closeAria: 'Close photo gallery',
      items: PHOTO_STACKS,
    },
    afterMbbs: {
      eyebrow: 'Life after MBBS in Vietnam',
      title: 'Plan the degree and the career pathway together.',
      description: 'The right counselling conversation includes licensing preparation, return-to-India planning, internship expectations, and realistic career direction.',
      items: AFTER_MBBS,
    },
    services: {
      eyebrow: 'Services and offerings',
      title: 'One team for counselling, paperwork, visa, and departure support.',
      items: SERVICES,
    },
    reviews: {
      eyebrow: 'Student counselling reviews',
      title: 'Professional guidance trusted by students and family coordinators.',
      description: 'Real counselling experiences from students and family coordinators who wanted clarity before choosing Vietnam for MBBS.',
      badge: 'Verified counselling review',
      items: REVIEWS,
    },
    cta: {
      title: 'Need a personalised college shortlist?',
      text: 'Share your profile and our counsellor will call with eligibility, fee, and document guidance.',
      button: 'Request counselling',
    },
    faq: {
      eyebrow: 'How do we help?',
      title: 'Common questions before a Vietnam MBBS decision.',
      items: FAQS.map(([question, answer]) => ({ question, answer })),
    },
    documents: {
      eyebrow: 'Document checklist',
      title: 'Keep the admission file clean before university submission.',
      description: 'Final requirements can differ by university and intake, but these documents help students and parents prepare the core Vietnam MBBS file early.',
      items: REQUIRED_DOCUMENTS,
    },
    contact: {
      eyebrow: 'MBBS Vietnam enquiry',
      title: 'Get official guidance for Vietnam MBBS admissions.',
      description: 'Submit details once. The same lead will appear in the admin panel for call tracking.',
      trustTitle: 'What you will get',
      trustItems: [
        ['profile', 'Eligibility check for Vietnam MBBS'],
        ['shortlist', 'College shortlist and fee discussion'],
        ['file', 'Admission timeline and document list'],
        ['visa', 'Visa, travel, and arrival guidance'],
      ],
      trustButton: 'Talk to counsellor',
    },
    footer: {
      brandTag: 'Vietnam MBBS guidance',
      description: 'Transparent counselling for Indian students planning MBBS in Vietnam: profile review, college shortlist, application, visa, travel, and parent support.',
      quickLinksTitle: 'Quick links',
      quickLinks: [
        ['Why Vietnam', '#why-vietnam'],
        ['Admission process', '#process'],
        ['Document checklist', '#documents'],
        ['College details', '#colleges'],
        ['Life in Vietnam', '#life'],
        ['Reviews', '#reviews'],
        ['Apply now', '#contact-section'],
      ],
      universitiesTitle: 'Vietnam universities',
      contactTitle: 'Contact',
      contactLinks: [
        { icon: 'phone', label: '+91 9452275173', href: 'tel:+919452275173' },
        { icon: 'mail', label: 'drsatyaoverseas@gmail.com', href: 'mailto:drsatyaoverseas@gmail.com' },
        { icon: 'whatsapp', label: 'WhatsApp counselling', href: 'https://wa.me/919452275173' },
        { icon: 'map', label: 'Pandit Purwa Rd, Shivaji Puram, Indira Nagar, Lucknow, Uttar Pradesh 226028', href: '#footer-map' },
      ],
      visitEyebrow: 'Visit us',
      visitTitle: 'Dr. Satya Overseas & Clinic',
      visitAddress: 'Pandit Purwa Rd, Shivaji Puram, Pandit Purava, Indira Nagar, Lucknow, Uttar Pradesh 226028',
      qrTitle: 'Scan for website',
      qrText: 'Open counselling page on mobile',
      bottomNote: 'University recognition, eligibility, fee, medium of instruction, and licensing rules must be verified for the current intake before admission.',
      copyright: 'Copyright 2026 Dr Satya Overseas',
    },
    floatingEnquiry: 'Enquiry',
  },
  hi: {
    seo: {
      title: 'भारतीय छात्रों के लिए वियतनाम में MBBS | Dr Satya Overseas',
      description: 'भारतीय छात्रों के लिए वियतनाम MBBS काउंसलिंग। वियतनाम मेडिकल यूनिवर्सिटी, फीस, हॉस्टल, वीज़ा, पात्रता, NEET मार्गदर्शन, एडमिशन प्रक्रिया और MBBS के बाद की योजना की तुलना करें।',
    },
    brandSub: 'आधिकारिक मार्गदर्शन - वियतनाम MBBS',
    header: {
      languageLabel: 'भाषा',
      callNow: 'अभी कॉल करें',
      brochure: 'ब्रोशर',
      applyNow: 'अभी आवेदन करें',
      whyVietnam: 'वियतनाम क्यों',
      colleges: 'कॉलेज',
      studentLife: 'स्टूडेंट लाइफ',
      reviews: 'रिव्यू',
    },
    form: {
      defaultInterest: 'वियतनाम में MBBS',
      sourceLabels: {
        Popup: 'पॉपअप पूछताछ',
        'Hero Eligibility Form': 'हीरो एलिजिबिलिटी फॉर्म',
        'Free Counselling': 'फ्री काउंसलिंग',
        'Download Brochure': 'ब्रोशर डाउनलोड',
        'Apply Now': 'अभी आवेदन करें',
        'Contact Us': 'संपर्क करें',
        'Trust Box CTA': 'ट्रस्ट बॉक्स पूछताछ',
        'Review CTA': 'शॉर्टलिस्ट CTA',
        'Floating Enquiry': 'फ्लोटिंग पूछताछ',
      },
      quickEligibilityTitle: 'त्वरित पात्रता जांच',
      contactTitle: 'अपनी पूछताछ भेजें',
      intro: 'अपनी जानकारी साझा करें, हमारी वियतनाम MBBS काउंसलर टीम आपको कॉल या WhatsApp करेगी।',
      fields: {
        name: 'पूरा नाम',
        phone: 'मोबाइल नंबर',
        email: 'ईमेल वैकल्पिक',
        interest: 'रुचि',
        question: 'प्रश्न वैकल्पिक',
      },
      placeholders: {
        name: 'आपका नाम',
        phone: '10 से 15 अंकों का नंबर',
        email: 'name@example.com',
        interest: 'वियतनाम में MBBS',
        question: 'फीस, हॉस्टल, पात्रता, वीज़ा या कॉलेज के बारे में पूछें',
      },
      validation: {
        invalidName: 'कृपया सही नाम दर्ज करें।',
        invalidPhone: 'कृपया सही मोबाइल नंबर दर्ज करें।',
        invalidEmail: 'कृपया सही ईमेल दर्ज करें या खाली छोड़ दें।',
      },
      submit: 'पूछताछ भेजें',
      submitting: 'भेजा जा रहा है...',
      success: 'पूछताछ सफलतापूर्वक भेजी गई। हमारी टीम जल्द आपसे संपर्क करेगी।',
      error: 'अभी सबमिट नहीं हो पा रहा है। कृपया थोड़ी देर बाद फिर कोशिश करें।',
      privacy: 'आपकी जानकारी केवल एडमिशन काउंसलिंग और फॉलो-अप के लिए उपयोग की जाएगी।',
      fallbackMessage: 'वियतनाम MBBS काउंसलिंग पूछताछ',
    },
    modal: {
      close: 'बंद करें',
      closeAria: 'पूछताछ फॉर्म बंद करें',
      title: 'वियतनाम MBBS मार्गदर्शन प्राप्त करें',
      text: 'अपना नंबर छोड़ें, हमारी काउंसलिंग टीम आपको कॉल या WhatsApp करेगी।',
    },
    hero: {
      badge: 'डायरेक्ट एडमिशन - MOU पार्टनर कॉलेज मार्गदर्शन',
      title: 'भारतीय छात्रों के लिए वियतनाम में MBBS, पूरी पारदर्शिता के साथ।',
      description: 'कॉलेज शॉर्टलिस्ट करें, फीस समझें, हॉस्टल और रहने का खर्च तुलना करें, और एडमिशन से लेकर वीज़ा व डिपार्चर तक पूरा मार्गदर्शन पाएं।',
      points: [
        ['profile', 'पात्रता और प्रोफाइल समीक्षा', 'NEET, PCB, बजट, दस्तावेज़ और उपयुक्त intake की जांच।'],
        ['wallet', 'फीस और टाइमलाइन की स्पष्टता', 'ट्यूशन, हॉस्टल, रहने का खर्च, आवेदन और वीज़ा चरण।'],
        ['checklist', 'एंड-टू-एंड सपोर्ट', 'ऑफर लेटर, वीज़ा फाइल, ट्रैवल, आगमन और परिवार समन्वय।'],
      ],
      primaryCta: 'फ्री काउंसलिंग लें',
      secondaryCta: 'ब्रोशर डाउनलोड करें',
      trust: [
        ['phone', '24h', 'कार्यदिवस प्रतिक्रिया'],
        ['checklist', 'Verified', 'कॉलेज शॉर्टलिस्टिंग'],
        ['wallet', 'Clear', 'फीस और प्रक्रिया'],
        ['visa', 'Visa', 'दस्तावेज़ मार्गदर्शन'],
      ],
    },
    whyVietnam: {
      eyebrow: 'वियतनाम क्यों',
      title: 'उन छात्रों के लिए, जो देश चुनने से पहले स्पष्टता चाहते हैं।',
      description: 'पहला निर्णय केवल यूनिवर्सिटी नहीं होता। इसमें बजट, पात्रता, भाषा माध्यम, दस्तावेज़, हॉस्टल, सुरक्षा और लाइसेंसिंग तैयारी भी शामिल होती है।',
      note: 'मान्यता, भाषा माध्यम, पात्रता, फीस और intake विवरण बदल सकते हैं। अंतिम काउंसलिंग में वर्तमान विश्वविद्यालय दस्तावेज़ अवश्य सत्यापित करें।',
      items: [
        {
          icon: 'wallet',
          title: 'सुलभ और योजनाबद्ध शिक्षा',
          text: 'वियतनाम MBBS के लिए एक संरचित विकल्प देता है, जहाँ आवेदन से पहले ट्यूशन, हॉस्टल, रहने का बजट और intake timeline स्पष्ट की जाती है।',
        },
        {
          icon: 'users',
          title: 'बढ़ता हुआ छात्र इकोसिस्टम',
          text: 'भारतीय छात्रों को दस्तावेज़, यात्रा, आगमन, आवास, भोजन और परिवार समन्वय में व्यावहारिक सहायता मिलती है।',
        },
        {
          icon: 'book',
          title: 'अंग्रेज़ी माध्यम मार्गदर्शन',
          text: 'प्रोग्राम भाषा, मान्यता और पात्रता संस्थान व intake के अनुसार बदलती है। Dr Satya Overseas शॉर्टलिस्ट से पहले इन्हें सत्यापित करता है।',
        },
      ],
    },
    process: {
      eyebrow: 'एडमिशन प्रक्रिया',
      title: 'पहली कॉल से कैंपस पहुंचने तक।',
      items: [
        {
          icon: 'profile',
          title: 'प्रोफाइल समीक्षा',
          text: 'NEET, PCB अंक, पासपोर्ट स्थिति, बजट और पसंदीदा intake पहले जांचे जाते हैं।',
        },
        {
          icon: 'shortlist',
          title: 'कॉलेज शॉर्टलिस्ट',
          text: 'छात्र को उपयुक्त वियतनाम यूनिवर्सिटी विकल्प फीस, हॉस्टल और timeline के साथ दिए जाते हैं।',
        },
        {
          icon: 'file',
          title: 'आवेदन और ऑफर लेटर',
          text: 'दस्तावेज़ तैयार होते हैं, आवेदन जमा होता है, और ऑफर लेटर प्रगति ट्रैक की जाती है।',
        },
        {
          icon: 'plane',
          title: 'वीज़ा और डिपार्चर',
          text: 'वीज़ा फाइल, यात्रा योजना, forex, packing और arrival समन्वय किया जाता है।',
        },
      ],
    },
    colleges: {
      eyebrow: 'कॉलेज विवरण',
      title: 'काउंसलिंग में चर्चा किए जाने वाले वियतनाम मेडिकल कॉलेज विकल्प।',
      description: 'अंतिम शॉर्टलिस्ट छात्र की प्रोफाइल, बजट, वर्तमान intake, दस्तावेज़ और विश्वविद्यालय के नवीनतम नियमों पर निर्भर करती है।',
      applyNow: 'अभी आवेदन करें',
      downloadBrochure: 'ब्रोशर',
      items: [
        {
          icon: 'college',
          image: ogImage,
          name: 'Can Tho University (CTUMP)',
          city: 'Can Tho',
          fee: 'संदर्भ फीस: 4 लाख / वर्ष',
          text: 'एक सरकारी विश्वविद्यालय विकल्प, जहाँ भारतीय पाठ्यक्रम चर्चा, शुरुआती क्लिनिकल exposure, आधुनिक लैब और छात्र समर्थन योजना पर काउंसलिंग की जाती है।',
          points: [
            '43 वर्ष पुराना विश्वविद्यालय प्रोफाइल',
            'वर्तमान NMC स्थिति की पुष्टि आवश्यक',
            'भारतीय पाठ्यक्रम पर चर्चा',
            'पहले वर्ष से क्लिनिकल exposure',
            'आधुनिक इन्फ्रास्ट्रक्चर और लैब',
            'एनाटॉमी लर्निंग संसाधनों की उपलब्धता',
            'हॉस्टल और भारतीय भोजन मार्गदर्शन',
            'भारत से लगभग 4 घंटे की उड़ान योजना',
          ],
        },
        {
          icon: 'hospital',
          image: groupimage,
          name: 'Phan Chau Trinh University (PCTU)',
          city: 'Da Nang',
          fee: 'संदर्भ फीस: 4.4 लाख / वर्ष',
          text: 'एक प्राइवेट विश्वविद्यालय विकल्प, जहाँ क्लिनिकल exposure, simulation facilities, university-owned hospitals और US-modeled curriculum संदर्भ पर चर्चा होती है।',
          points: [
            'शीर्ष प्राइवेट विश्वविद्यालय प्रोफाइल',
            'वर्तमान NMC स्थिति की पुष्टि आवश्यक',
            'US-modeled curriculum संदर्भ',
            'NEXT और USMLE ओरिएंटेशन',
            'पहले वर्ष से क्लिनिकल exposure',
            'Simulation hospital और research centre',
            'विश्वविद्यालय-स्वामित्व वाले अस्पताल',
            'हैंड्स-ऑन एनाटॉमी और practical exposure',
          ],
        },
        {
          icon: 'city',
          image: twitterImage,
          name: 'Dai Nam University (DNU)',
          city: 'Hanoi',
          fee: 'संदर्भ फीस: 3.5 लाख / वर्ष',
          text: 'हनोई स्थित एक प्राइवेट विश्वविद्यालय विकल्प, जहाँ NEXT readiness, affiliated hospitals और किफायती योजना पर ध्यान दिया जाता है।',
          points: [
            'हनोई का प्रमुख प्राइवेट विश्वविद्यालय',
            'वर्तमान NMC स्थिति की पुष्टि आवश्यक',
            'NEXT परीक्षा तैयारी पर चर्चा',
            'विश्वविद्यालय-संबद्ध अस्पताल',
            'सुलभ ट्यूशन योजना',
            'हनोई शहर और सुरक्षा जानकारी',
            'हॉस्टल और भारतीय भोजन चर्चा',
            'यात्रा और दस्तावेज़ मार्गदर्शन',
          ],
        },
        {
          icon: 'medical',
          image: frontimage,
          name: 'Buon Ma Thuot University (BMTU)',
          city: 'Central Highlands',
          fee: 'संदर्भ फीस: 3.8 लाख / वर्ष',
          text: 'एक मेडिकल विश्वविद्यालय विकल्प, जहाँ ऑन-कैंपस अस्पताल exposure, outpatient volume और multi-hospital affiliations पर काउंसलिंग की जाती है।',
          points: [
            'बड़ा प्राइवेट मेडिकल विश्वविद्यालय प्रोफाइल',
            '500+ बेड मल्टी-स्पेशियलिटी अस्पताल कैंपस संदर्भ',
            'सरकारी स्तर के outpatient exposure की चर्चा',
            'सरकारी और प्राइवेट अस्पताल affiliations',
            'भारतीय छात्र प्रवेश मार्गदर्शन',
            'लड़के और लड़कियों के लिए अलग हॉस्टल योजना',
            'Simulation lab और hands-on training',
            'शैक्षणिक प्रदर्शन आधारित scholarship चर्चा',
          ],
        },
        {
          icon: 'campus',
          image: ogImage,
          name: 'Dong A University (UDA)',
          city: 'Da Nang',
          fee: 'संदर्भ फीस: $4,500 / वर्ष',
          text: 'Da Nang का विश्वविद्यालय विकल्प, जहाँ city comfort, NEXT readiness और hospital partnership संदर्भ पर चर्चा की जाती है।',
          points: [
            'Da Nang का शीर्ष प्राइवेट विश्वविद्यालय',
            'वर्तमान NMC स्थिति की पुष्टि आवश्यक',
            'NEXT परीक्षा readiness चर्चा',
            'VinMec Hospital partnership संदर्भ',
            'Da Nang शहर comfort overview',
            'हॉस्टल और भोजन योजना',
            'यात्रा और arrival सहायता',
            'भारत से लगभग 5 घंटे की उड़ान योजना',
          ],
        },
      ],
    },
    life: {
      eyebrow: 'वियतनाम में जीवन',
      title: 'कैंपस लाइफ, हॉस्टल, भोजन, शहर की सुविधा और परिवार अपडेट महत्वपूर्ण हैं।',
      description: 'छात्र कई वर्षों के लिए दूसरे देश जा रहे होते हैं। काउंसलिंग में रोज़मर्रा के जीवन को भी एडमिशन दस्तावेज़ों जितनी गंभीरता से कवर करना चाहिए।',
      items: [
        {
          icon: 'college',
          title: 'कॉलेज जीवन',
          text: 'छात्र कक्षा अध्ययन, लैब, अस्पताल विज़िट, समूह अध्ययन और विश्वविद्यालय कार्यक्रमों के साथ धीरे-धीरे अनुकूल होते हैं। शुरुआती सप्ताह settling-in पर केंद्रित रहते हैं।',
        },
        {
          icon: 'home',
          title: 'हॉस्टल और भोजन',
          text: 'रहने की व्यवस्था, मेस या self-cooking विकल्प, आसपास भारतीय भोजन और दैनिक बजट पर पहले से चर्चा की जाती है।',
        },
        {
          icon: 'city',
          title: 'शहर का जीवन',
          text: 'वियतनाम साफ-सुथरे शहर, पब्लिक ट्रांसपोर्ट, कैफे और छात्र-अनुकूल क्षेत्रों के साथ मेडिकल शिक्षा के लिए शांत माहौल देता है।',
        },
        {
          icon: 'heart',
          title: 'परिवार समन्वय',
          text: 'परिवारों को भरोसेमंद अपडेट चाहिए होते हैं। Dr Satya Overseas एडमिशन से लेकर arrival और शुरुआती adjustment तक प्रक्रिया स्पष्ट रखता है।',
        },
      ],
    },
    photoStacks: {
      eyebrow: 'स्टूडेंट फोटो स्टोरीज़',
      title: 'हर स्टैक खोलकर वियतनाम जीवन, कॉलेज, लैब, हॉस्टल और क्लिनिकल exposure को देखें।',
      description: 'ये फोटो स्टैक आपके वास्तविक छात्र फोटो के लिए तैयार हैं। हर स्टैक एक गैलरी खोलता है ताकि छात्र और परिवार वातावरण को बेहतर समझ सकें।',
      modalEyebrow: 'फोटो स्टैक',
      closeAria: 'फोटो गैलरी बंद करें',
      items: [
        {
          icon: 'globe',
          title: 'वियतनाम में जीवन',
          text: 'शहर की सुविधा, यात्रा, भोजन, सुरक्षा, स्थानीय संस्कृति और छात्र दिनचर्या।',
          cover: frontimage,
          photos: [
            { src: frontimage, caption: 'वियतनाम कैंपस वातावरण और छात्र arrival support' },
            { src: groupimage, caption: 'भारतीय छात्र समुदाय और काउंसलिंग support' },
            { src: ogImage, caption: 'विश्वविद्यालय कार्यक्रम और guided student interactions' },
          ],
        },
        {
          icon: 'college',
          title: 'कॉलेज जीवन',
          text: 'कक्षाएँ, peer learning, campus events, mentoring और academic discipline.',
          cover: groupimage,
          photos: [
            { src: groupimage, caption: 'स्टूडेंट ग्रुप और कैंपस interaction' },
            { src: ogImage, caption: 'कॉलेज समारोह और शैक्षणिक वातावरण' },
            { src: twitterImage, caption: 'स्टूडेंट टीम learning और यूनिवर्सिटी गतिविधियाँ' },
          ],
        },
        {
          icon: 'medical',
          title: 'मशीनें और लैब',
          text: 'Simulation, anatomy, clinical equipment, lab exposure और practical learning.',
          cover: ogImage,
          photos: [
            { src: ogImage, caption: 'मेडिकल यूनिवर्सिटी इवेंट और learning infrastructure' },
            { src: twitterImage, caption: 'Hands-on learning और guided medical exposure' },
            { src: frontimage, caption: 'कैंपस सुविधाएँ और आधुनिक academic setup' },
          ],
        },
        {
          icon: 'home',
          title: 'हॉस्टल और स्टूडेंट लाइफ',
          text: 'रहने की व्यवस्था, भोजन, दैनिक बजट, भारतीय भोजन विकल्प और settling-in support.',
          cover: twitterImage,
          photos: [
            { src: twitterImage, caption: 'स्टूडेंट लाइफ और support environment' },
            { src: groupimage, caption: 'Peer group और community comfort' },
            { src: frontimage, caption: 'यूनिवर्सिटी surroundings और student movement' },
          ],
        },
        {
          icon: 'hospital',
          title: 'क्लिनिकल exposure',
          text: 'अस्पताल विज़िट, शुरुआती exposure, patient-care understanding और career discipline.',
          cover: frontimage,
          photos: [
            { src: frontimage, caption: 'कैंपस और hospital exposure planning' },
            { src: ogImage, caption: 'यूनिवर्सिटी स्तर का medical learning environment' },
            { src: twitterImage, caption: 'शैक्षणिक गतिविधियों में छात्र भागीदारी' },
          ],
        },
      ],
    },
    afterMbbs: {
      eyebrow: 'वियतनाम में MBBS के बाद',
      title: 'डिग्री और करियर पाथवे की योजना साथ में बनाएं।',
      description: 'सही काउंसलिंग में licensing preparation, भारत वापसी योजना, internship expectations और realistic career direction शामिल होना चाहिए।',
      items: [
        {
          icon: 'india',
          title: 'भारत वापसी की योजना',
          text: 'कॉलेज चुनने से पहले छात्रों को भारत की नवीनतम licensing pathway, internship requirements और exam expectations समझनी चाहिए।',
        },
        {
          icon: 'target',
          title: 'FMGE या NExT तैयारी की सोच',
          text: 'गंभीर अध्ययन योजना पहले वर्ष से शुरू होती है। काउंसलिंग बताती है कि clinical discipline और regular revision क्यों जरूरी हैं।',
        },
        {
          icon: 'globe',
          title: 'PG और global options',
          text: 'MBBS के बाद करियर दिशा नियमों, exam performance, internship, भाषा और देश-विशिष्ट licensing rules पर निर्भर करती है।',
        },
        {
          icon: 'compass',
          title: 'व्यावहारिक करियर मार्गदर्शन',
          text: 'कोई shortcut वादा नहीं। छात्रों को मेहनत, timelines, documentation और licensing responsibilities की वास्तविक तस्वीर दी जाती है।',
        },
      ],
    },
    services: {
      eyebrow: 'सेवाएँ और ऑफरिंग्स',
      title: 'काउंसलिंग, paperwork, visa और departure support के लिए एक ही टीम।',
      items: [
        { icon: 'profile', title: 'फ्री पात्रता काउंसलिंग' },
        { icon: 'shortlist', title: 'वियतनाम कॉलेज शॉर्टलिस्टिंग' },
        { icon: 'wallet', title: 'फीस और living cost की समझ' },
        { icon: 'file', title: 'आवेदन और ऑफर लेटर सहायता' },
        { icon: 'visa', title: 'वीज़ा फाइल और यात्रा मार्गदर्शन' },
        { icon: 'checklist', title: 'Forex, packing और arrival checklist' },
        { icon: 'users', title: 'परिवार समन्वय' },
        { icon: 'heart', title: 'पोस्ट-arrival छात्र support' },
      ],
    },
    reviews: {
      eyebrow: 'स्टूडेंट काउंसलिंग रिव्यू',
      title: 'छात्रों और परिवार समन्वयकों का भरोसेमंद पेशेवर मार्गदर्शन।',
      description: 'वियतनाम MBBS चुनने से पहले स्पष्टता चाहने वाले छात्रों और परिवार समन्वयकों के वास्तविक अनुभव।',
      badge: 'सत्यापित काउंसलिंग रिव्यू',
      items: [
        {
          name: 'Aryan Singh',
          location: 'Lucknow',
          role: 'Student',
          photo: aryanSingh,
          text: 'Dr Satya Overseas ने मुझे वियतनाम MBBS विकल्पों को पूरी स्पष्टता के साथ समझाया। काउंसलिंग व्यावहारिक, पारदर्शी और सही यूनिवर्सिटी चयन पर केंद्रित थी।',
        },
        {
          name: 'Chirag Gupta',
          location: 'New Delhi',
          role: 'Student',
          photo: chiragGupta,
          text: 'टीम ने एडमिशन प्रक्रिया को step by step समझाया और कॉलेज, दस्तावेज़, वीज़ा और timeline की वास्तविक तस्वीर दी। इससे निर्णय आसान हो गया।',
        },
        {
          name: 'Narayan Shukla',
          location: 'Lucknow',
          role: 'Student',
          photo: narayanShukla,
          text: 'मुझे फीस, हॉस्टल, सुरक्षा और पूरी एडमिशन प्रक्रिया पर स्पष्ट मार्गदर्शन मिला। पूरी काउंसलिंग जिम्मेदार और प्रोफेशनल लगी।',
        },
        {
          name: 'Pankhuri Shukla',
          location: 'Lucknow',
          role: 'Technical professional',
          photo: pankhuriShukla,
          text: 'मैं अपने भाई के एडमिशन को coordinate कर रही थी और मुझे स्पष्ट व व्यावहारिक जवाब चाहिए थे। Dr Satya Overseas ने यूनिवर्सिटी विकल्प, दस्तावेज़, timeline और fee planning बहुत प्रोफेशनल तरीके से समझाई।',
        },
        {
          name: 'Ritu Kushwaha',
          location: 'New Delhi',
          role: 'Student',
          photo: rituKushwaha,
          text: 'काउंसलर ने सुरक्षा, accommodation, food, travel और student support से जुड़े मेरे सवालों का शांत और भरोसेमंद तरीके से जवाब दिया।',
        },
        {
          name: 'Shaurya Verma',
          location: 'Lucknow',
          role: 'Student',
          photo: shauryaVerma,
          text: 'सबसे अच्छी बात ईमानदार काउंसलिंग रही। मुझे वियतनाम MBBS, यूनिवर्सिटी चयन, वीज़ा support और admission के बाद की तैयारी की स्पष्ट समझ मिली।',
        },
      ],
    },
    cta: {
      title: 'क्या आपको personalized college shortlist चाहिए?',
      text: 'अपनी प्रोफाइल साझा करें, हमारी काउंसलर टीम पात्रता, फीस और दस्तावेज़ मार्गदर्शन के साथ कॉल करेगी।',
      button: 'काउंसलिंग अनुरोध करें',
    },
    faq: {
      eyebrow: 'हम कैसे मदद करते हैं?',
      title: 'वियतनाम MBBS निर्णय से पहले सामान्य प्रश्न।',
      items: [
        {
          question: 'क्या वियतनाम MBBS भारतीय छात्रों के लिए अच्छा विकल्प है?',
          answer: 'हाँ, यदि विश्वविद्यालय, भाषा माध्यम, मान्यता, बजट और licensing pathway की वर्तमान intake के अनुसार सही पुष्टि की जाए।',
        },
        {
          question: 'अगर NEET qualify नहीं हुआ तो क्या विदेश में MBBS कर सकते हैं?',
          answer: 'भारतीय छात्रों को किसी भी foreign MBBS program से पहले नवीनतम NEET और NMC eligibility rules की पुष्टि करनी चाहिए। काउंसलिंग retake planning और अगले intake strategy में मदद करती है।',
        },
        {
          question: 'वियतनाम में MBBS की फीस कितनी होती है?',
          answer: 'फीस विश्वविद्यालय और intake के अनुसार बदलती है। काउंसलिंग में tuition, hostel, food, visa, travel, insurance और living cost का पूरा budget बताया जाता है।',
        },
        {
          question: 'क्या Dr Satya Overseas दस्तावेज़ों में मदद करता है?',
          answer: 'हाँ। काउंसलिंग flow में document checklist, application support, visa file guidance और departure preparation शामिल हैं।',
        },
        {
          question: 'क्या माता-पिता या परिवार भी काउंसलर से बात कर सकते हैं?',
          answer: 'हाँ। fee planning, hostel, safety, travel और long-term career clarity के लिए परिवार से चर्चा महत्वपूर्ण होती है।',
        },
        {
          question: 'क्या आप result या licensing की guarantee देते हैं?',
          answer: 'नहीं। कोई भी genuine counsellor licensing result की guarantee नहीं देता। छात्रों को नियमों के अनुसार गंभीर तैयारी करनी होती है।',
        },
      ],
    },
    documents: {
      eyebrow: 'दस्तावेज़ चेकलिस्ट',
      title: 'यूनिवर्सिटी submission से पहले एडमिशन फाइल साफ रखें।',
      description: 'अंतिम requirements विश्वविद्यालय और intake के अनुसार अलग हो सकती हैं, लेकिन ये दस्तावेज़ प्रारंभिक MBBS फाइल तैयार करने में मदद करते हैं।',
      items: [
        { title: '10वीं की मार्कशीट', text: 'छात्र की 10वीं मार्कशीट की स्पष्ट scan या फोटो कॉपी रखें।' },
        { title: 'हाई स्कूल सर्टिफिकेट', text: 'अकादमिक verification के लिए हाई स्कूल सर्टिफिकेट की साफ कॉपी रखें।' },
        { title: '12वीं की मार्कशीट', text: 'छात्र की 12वीं मार्कशीट की स्पष्ट scan या फोटो कॉपी रखें।' },
        { title: 'ट्रांसफर सर्टिफिकेट (TC)', text: 'अंतिम स्कूल द्वारा जारी ट्रांसफर सर्टिफिकेट रखें।' },
        { title: 'स्टूडेंट पासपोर्ट (3 पेज)', text: 'एडमिशन और वीज़ा फाइल के लिए पासपोर्ट की आवश्यक कॉपी रखें।' },
        { title: 'स्टूडेंट बर्थ सर्टिफिकेट', text: 'जन्म प्रमाणपत्र में विवरण पासपोर्ट और शैक्षणिक दस्तावेज़ से मेल खाना चाहिए।' },
        { title: 'स्टूडेंट आधार कार्ड', text: 'दस्तावेज़ और रिकॉर्ड मिलान के लिए छात्र का पहचान प्रमाण।' },
        { title: 'स्टूडेंट PAN कार्ड', text: 'जहाँ आवश्यक हो वहाँ छात्र के PAN कार्ड की कॉपी रखें।' },
        { title: 'स्टूडेंट पासपोर्ट साइज फोटो', text: 'साफ बैकग्राउंड वाली हाल की पासपोर्ट साइज फोटो रखें।' },
        { title: 'पिता का आधार कार्ड', text: 'छात्र एडमिशन रिकॉर्ड के लिए पिता का पहचान प्रमाण।' },
        { title: 'माता का आधार कार्ड', text: 'छात्र एडमिशन रिकॉर्ड के लिए माता का पहचान प्रमाण।' },
        { title: 'पिता का PAN कार्ड', text: 'फाइनेंशियल और sponsor documentation के लिए पिता के PAN कार्ड की कॉपी।' },
        { title: 'माता का PAN कार्ड', text: 'फाइनेंशियल और sponsor documentation के लिए माता के PAN कार्ड की कॉपी।' },
      ],
    },
    contact: {
      eyebrow: 'वियतनाम MBBS पूछताछ',
      title: 'वियतनाम MBBS एडमिशन के लिए आधिकारिक मार्गदर्शन प्राप्त करें।',
      description: 'एक बार विवरण जमा करें। यही lead admin panel में कॉल ट्रैकिंग के लिए दिखाई देगी।',
      trustTitle: 'आपको क्या मिलेगा',
      trustItems: [
        ['profile', 'वियतनाम MBBS के लिए पात्रता जांच'],
        ['shortlist', 'कॉलेज शॉर्टलिस्ट और फीस चर्चा'],
        ['file', 'एडमिशन timeline और दस्तावेज़ सूची'],
        ['visa', 'वीज़ा, यात्रा और arrival guidance'],
      ],
      trustButton: 'काउंसलर से बात करें',
    },
    footer: {
      brandTag: 'वियतनाम MBBS मार्गदर्शन',
      description: 'वियतनाम में MBBS की योजना बनाने वाले भारतीय छात्रों के लिए पारदर्शी काउंसलिंग: प्रोफाइल समीक्षा, कॉलेज शॉर्टलिस्ट, आवेदन, वीज़ा, यात्रा और परिवार support.',
      quickLinksTitle: 'त्वरित लिंक',
      quickLinks: [
        ['वियतनाम क्यों', '#why-vietnam'],
        ['एडमिशन प्रक्रिया', '#process'],
        ['दस्तावेज़ चेकलिस्ट', '#documents'],
        ['कॉलेज विवरण', '#colleges'],
        ['वियतनाम में जीवन', '#life'],
        ['रिव्यू', '#reviews'],
        ['अभी आवेदन करें', '#contact-section'],
      ],
      universitiesTitle: 'वियतनाम विश्वविद्यालय',
      contactTitle: 'संपर्क',
      contactLinks: [
        { icon: 'phone', label: '+91 9452275173', href: 'tel:+919452275173' },
        { icon: 'mail', label: 'drsatyaoverseas@gmail.com', href: 'mailto:drsatyaoverseas@gmail.com' },
        { icon: 'whatsapp', label: 'WhatsApp काउंसलिंग', href: 'https://wa.me/919452275173' },
        { icon: 'map', label: 'Pandit Purwa Rd, Shivaji Puram, Indira Nagar, Lucknow, Uttar Pradesh 226028', href: '#footer-map' },
      ],
      visitEyebrow: 'हमसे मिलें',
      visitTitle: 'Dr. Satya Overseas & Clinic',
      visitAddress: 'Pandit Purwa Rd, Shivaji Puram, Pandit Purava, Indira Nagar, Lucknow, Uttar Pradesh 226028',
      qrTitle: 'वेबसाइट स्कैन करें',
      qrText: 'मोबाइल पर काउंसलिंग पेज खोलें',
      bottomNote: 'यूनिवर्सिटी मान्यता, पात्रता, फीस, शिक्षण माध्यम और licensing rules की वर्तमान intake के लिए पुष्टि आवश्यक है।',
      copyright: 'कॉपीराइट 2026 Dr Satya Overseas',
    },
    floatingEnquiry: 'पूछताछ',
  },
  vi: {
    seo: {
      title: 'MBBS tại Việt Nam cho sinh viên Ấn Độ | Dr Satya Overseas',
      description: 'Tư vấn MBBS tại Việt Nam cho sinh viên Ấn Độ. So sánh trường y tại Việt Nam, học phí, ký túc xá, visa, điều kiện, hướng dẫn NEET, quy trình nhập học và kế hoạch sau MBBS.',
    },
    brandSub: 'Hướng dẫn chính thức - MBBS tại Việt Nam',
    header: {
      languageLabel: 'Ngôn ngữ',
      callNow: 'Gọi ngay',
      brochure: 'Brochure',
      applyNow: 'Đăng ký ngay',
      whyVietnam: 'Vì sao Việt Nam',
      colleges: 'Trường',
      studentLife: 'Đời sống sinh viên',
      reviews: 'Đánh giá',
    },
    form: {
      defaultInterest: 'MBBS tại Việt Nam',
      sourceLabels: {
        Popup: 'Biểu mẫu popup',
        'Hero Eligibility Form': 'Biểu mẫu kiểm tra điều kiện',
        'Free Counselling': 'Tư vấn miễn phí',
        'Download Brochure': 'Tải brochure',
        'Apply Now': 'Đăng ký ngay',
        'Contact Us': 'Liên hệ',
        'Trust Box CTA': 'Yêu cầu tư vấn',
        'Review CTA': 'CTA shortlist',
        'Floating Enquiry': 'Yêu cầu nổi',
      },
      quickEligibilityTitle: 'Kiểm tra điều kiện nhanh',
      contactTitle: 'Gửi yêu cầu của bạn',
      intro: 'Hãy chia sẻ thông tin, đội ngũ tư vấn MBBS tại Việt Nam sẽ gọi hoặc nhắn WhatsApp cho bạn.',
      fields: {
        name: 'Họ và tên',
        phone: 'Số điện thoại',
        email: 'Email (không bắt buộc)',
        interest: 'Nhu cầu',
        question: 'Câu hỏi (không bắt buộc)',
      },
      placeholders: {
        name: 'Tên của bạn',
        phone: 'Số từ 10 đến 15 chữ số',
        email: 'name@example.com',
        interest: 'MBBS tại Việt Nam',
        question: 'Hỏi về học phí, ký túc xá, điều kiện, visa hoặc trường học',
      },
      validation: {
        invalidName: 'Vui lòng nhập tên hợp lệ.',
        invalidPhone: 'Vui lòng nhập số điện thoại hợp lệ.',
        invalidEmail: 'Vui lòng nhập email hợp lệ hoặc để trống.',
      },
      submit: 'Gửi yêu cầu',
      submitting: 'Đang gửi...',
      success: 'Yêu cầu đã được gửi thành công. Đội ngũ của chúng tôi sẽ liên hệ với bạn sớm.',
      error: 'Không thể gửi lúc này. Vui lòng thử lại sau.',
      privacy: 'Thông tin của bạn chỉ được dùng cho tư vấn tuyển sinh và theo dõi.',
      fallbackMessage: 'Yêu cầu tư vấn MBBS tại Việt Nam',
    },
    modal: {
      close: 'Đóng',
      closeAria: 'Đóng biểu mẫu yêu cầu',
      title: 'Nhận hướng dẫn MBBS tại Việt Nam',
      text: 'Để lại số điện thoại, đội ngũ tư vấn sẽ gọi hoặc nhắn WhatsApp cho bạn.',
    },
    hero: {
      badge: 'Tuyển sinh trực tiếp - hướng dẫn trường đối tác MOU',
      title: 'MBBS tại Việt Nam cho sinh viên Ấn Độ với tư vấn minh bạch.',
      description: 'Lựa chọn trường phù hợp, hiểu rõ học phí, so sánh ký túc xá và chi phí sinh hoạt, đồng thời hoàn tất nhập học, visa và khởi hành cùng Dr Satya Overseas.',
      points: [
        ['profile', 'Đánh giá hồ sơ và điều kiện', 'Kiểm tra NEET, PCB, ngân sách, giấy tờ và kỳ nhập học phù hợp.'],
        ['wallet', 'Rõ ràng về học phí và tiến độ', 'Học phí, ký túc xá, sinh hoạt, nộp hồ sơ và các bước visa.'],
        ['checklist', 'Hỗ trợ trọn gói', 'Thư mời nhập học, hồ sơ visa, di chuyển, đón sân bay và phối hợp với gia đình.'],
      ],
      primaryCta: 'Nhận tư vấn miễn phí',
      secondaryCta: 'Tải brochure',
      trust: [
        ['phone', '24h', 'Phản hồi trong ngày làm việc'],
        ['checklist', 'Xác minh', 'Lọc trường phù hợp'],
        ['wallet', 'Rõ ràng', 'Học phí và quy trình'],
        ['visa', 'Visa', 'Hướng dẫn giấy tờ'],
      ],
    },
    whyVietnam: {
      eyebrow: 'Vì sao Việt Nam',
      title: 'Dành cho những sinh viên muốn có sự rõ ràng trước khi chọn quốc gia.',
      description: 'Quyết định đầu tiên không chỉ là trường đại học. Đó còn là ngân sách, điều kiện, ngôn ngữ giảng dạy, giấy tờ, ký túc xá, an toàn và lộ trình cấp phép.',
      note: 'Công nhận, ngôn ngữ giảng dạy, điều kiện, học phí và thông tin kỳ nhập học có thể thay đổi. Hãy xác minh giấy tờ trường ở thời điểm hiện tại trước khi nhập học.',
      items: [
        {
          icon: 'wallet',
          title: 'Chi phí hợp lý và có kế hoạch',
          text: 'Việt Nam mang đến lộ trình MBBS có cấu trúc, với tư vấn rõ ràng về học phí, ký túc xá, chi phí sinh hoạt và tiến độ nhập học trước khi nộp hồ sơ.',
        },
        {
          icon: 'users',
          title: 'Hệ sinh thái sinh viên đang phát triển',
          text: 'Sinh viên Ấn Độ nhận được hỗ trợ thực tế về giấy tờ, di chuyển, đến nơi, chỗ ở, ăn uống và phối hợp với gia đình.',
        },
        {
          icon: 'book',
          title: 'Hướng dẫn về chương trình tiếng Anh',
          text: 'Ngôn ngữ chương trình, công nhận và điều kiện thay đổi theo trường và kỳ nhập học. Dr Satya Overseas xác minh trước khi shortlist.',
        },
      ],
    },
    process: {
      eyebrow: 'Quy trình nhập học',
      title: 'Từ cuộc gọi đầu tiên đến khi tới trường.',
      items: [
        {
          icon: 'profile',
          title: 'Đánh giá hồ sơ',
          text: 'NEET, điểm PCB, tình trạng hộ chiếu, ngân sách và kỳ nhập học mong muốn được kiểm tra trước.',
        },
        {
          icon: 'shortlist',
          title: 'Lọc trường phù hợp',
          text: 'Sinh viên nhận được các lựa chọn trường y tại Việt Nam với thông tin rõ ràng về học phí, ký túc xá và tiến độ.',
        },
        {
          icon: 'file',
          title: 'Nộp hồ sơ và thư mời',
          text: 'Giấy tờ được chuẩn bị, hồ sơ được nộp và tiến trình thư mời nhập học được theo dõi.',
        },
        {
          icon: 'plane',
          title: 'Visa và khởi hành',
          text: 'Hồ sơ visa, kế hoạch di chuyển, forex, chuẩn bị hành lý và đón tới nơi được hỗ trợ đầy đủ.',
        },
      ],
    },
    colleges: {
      eyebrow: 'Thông tin trường',
      title: 'Các lựa chọn trường y tại Việt Nam được thảo luận trong quá trình tư vấn.',
      description: 'Danh sách trường cuối cùng phụ thuộc vào hồ sơ, ngân sách, kỳ nhập học hiện tại, giấy tờ và quy định mới nhất của từng trường.',
      applyNow: 'Đăng ký ngay',
      downloadBrochure: 'Brochure',
      items: [
        {
          icon: 'college',
          image: ogImage,
          name: 'Can Tho University (CTUMP)',
          city: 'Can Tho',
          fee: 'Học phí tham khảo: 4 lakh / năm',
          text: 'Một lựa chọn đại học công lập lâu đời, được tư vấn với trọng tâm vào chương trình phù hợp sinh viên Ấn Độ, tiếp xúc lâm sàng sớm, phòng lab hiện đại và kế hoạch hỗ trợ sinh viên.',
          points: [
            'Trường đại học có lịch sử 43 năm',
            'Cần xác minh tình trạng NMC hiện tại',
            'Thảo luận về chương trình phù hợp sinh viên Ấn Độ',
            'Tiếp xúc lâm sàng từ năm đầu',
            'Cơ sở vật chất và phòng lab hiện đại',
            'Tiếp cận tài nguyên học giải phẫu',
            'Hướng dẫn ký túc xá và đồ ăn Ấn Độ',
            'Lập kế hoạch bay khoảng 4 giờ từ Ấn Độ',
          ],
        },
        {
          icon: 'hospital',
          image: groupimage,
          name: 'Phan Chau Trinh University (PCTU)',
          city: 'Da Nang',
          fee: 'Học phí tham khảo: 4.4 lakh / năm',
          text: 'Một lựa chọn trường tư với tư vấn về tiếp xúc lâm sàng, mô phỏng, bệnh viện thuộc trường và định hướng chương trình theo mô hình Mỹ.',
          points: [
            'Hồ sơ trường tư nổi bật',
            'Cần xác minh tình trạng NMC hiện tại',
            'Tham chiếu chương trình theo mô hình Mỹ',
            'Định hướng chuẩn bị NEXT và USMLE',
            'Tiếp xúc lâm sàng từ năm đầu',
            'Bệnh viện mô phỏng và trung tâm nghiên cứu',
            'Bệnh viện thuộc sở hữu trường',
            'Thực hành giải phẫu và kỹ năng thực tế',
          ],
        },
        {
          icon: 'city',
          image: twitterImage,
          name: 'Dai Nam University (DNU)',
          city: 'Hanoi',
          fee: 'Học phí tham khảo: 3.5 lakh / năm',
          text: 'Một lựa chọn tại Hà Nội với định hướng về chuẩn bị NEXT, bệnh viện liên kết và kế hoạch chi phí hợp lý.',
          points: [
            'Trường tư nổi bật tại Hà Nội',
            'Cần xác minh tình trạng NMC hiện tại',
            'Thảo luận về chuẩn bị NEXT',
            'Bệnh viện liên kết với trường',
            'Kế hoạch học phí hợp lý',
            'Tổng quan về thành phố Hà Nội và an toàn',
            'Trao đổi về ký túc xá và đồ ăn Ấn Độ',
            'Hướng dẫn di chuyển và giấy tờ',
          ],
        },
        {
          icon: 'medical',
          image: frontimage,
          name: 'Buon Ma Thuot University (BMTU)',
          city: 'Central Highlands',
          fee: 'Học phí tham khảo: 3.8 lakh / năm',
          text: 'Một lựa chọn trường y với tư vấn về bệnh viện trong khuôn viên, lưu lượng bệnh nhân ngoại trú và liên kết với nhiều bệnh viện.',
          points: [
            'Hồ sơ trường y tư lớn',
            'Tham chiếu bệnh viện đa khoa 500+ giường trong khuôn viên',
            'Trao đổi về lượng bệnh nhân ngoại trú cấp chính phủ',
            'Liên kết bệnh viện công và tư',
            'Hướng dẫn cho sinh viên Ấn Độ',
            'Kế hoạch ký túc xá riêng cho nam và nữ',
            'Phòng lab mô phỏng và đào tạo thực hành',
            'Trao đổi học bổng dựa trên thành tích',
          ],
        },
        {
          icon: 'campus',
          image: ogImage,
          name: 'Dong A University (UDA)',
          city: 'Da Nang',
          fee: 'Học phí tham khảo: $4,500 / năm',
          text: 'Một lựa chọn tại Đà Nẵng được tư vấn với trọng tâm vào môi trường thành phố, chuẩn bị NEXT và các liên kết bệnh viện.',
          points: [
            'Trường tư nổi bật tại Đà Nẵng',
            'Cần xác minh tình trạng NMC hiện tại',
            'Trao đổi về sự sẵn sàng cho NEXT',
            'Tham chiếu hợp tác với VinMec Hospital',
            'Tổng quan về sự tiện nghi tại Đà Nẵng',
            'Kế hoạch ký túc xá và ăn uống',
            'Hỗ trợ di chuyển và đến nơi',
            'Lập kế hoạch bay khoảng 5 giờ từ Ấn Độ',
          ],
        },
      ],
    },
    life: {
      eyebrow: 'Đời sống tại Việt Nam',
      title: 'Đời sống campus, ký túc xá, ăn uống, thành phố và cập nhật cho gia đình đều quan trọng.',
      description: 'Sinh viên sẽ sống ở một quốc gia khác trong nhiều năm. Vì vậy, tư vấn cần bao gồm cả đời sống hàng ngày với mức độ nghiêm túc như giấy tờ nhập học.',
      items: [
        {
          icon: 'college',
          title: 'Cuộc sống ở trường',
          text: 'Sinh viên sẽ dần thích nghi với lớp học, phòng lab, thăm bệnh viện, học nhóm và các hoạt động của trường trong những tuần đầu.',
        },
        {
          icon: 'home',
          title: 'Ký túc xá và ăn uống',
          text: 'Chỗ ở, lựa chọn ăn tại căng tin hay tự nấu, đồ ăn Ấn Độ gần trường và ngân sách hàng ngày đều được trao đổi trước khi khởi hành.',
        },
        {
          icon: 'city',
          title: 'Đời sống thành phố',
          text: 'Việt Nam có các thành phố sạch, phương tiện công cộng, quán cà phê và khu vực thân thiện với sinh viên, phù hợp cho học tập y khoa.',
        },
        {
          icon: 'heart',
          title: 'Phối hợp với gia đình',
          text: 'Gia đình cần cập nhật đáng tin cậy. Dr Satya Overseas giữ quy trình rõ ràng từ nhập học đến khi sinh viên ổn định ban đầu.',
        },
      ],
    },
    photoStacks: {
      eyebrow: 'Bộ ảnh sinh viên',
      title: 'Mở từng bộ ảnh để xem đời sống tại Việt Nam, trường học, phòng lab, ký túc xá và môi trường lâm sàng.',
      description: 'Các bộ ảnh này sẵn sàng cho hình ảnh thực tế của sinh viên. Mỗi bộ sẽ mở một thư viện ảnh để phụ huynh và sinh viên hiểu rõ môi trường hơn.',
      modalEyebrow: 'Bộ ảnh',
      closeAria: 'Đóng thư viện ảnh',
      items: [
        {
          icon: 'globe',
          title: 'Cuộc sống tại Việt Nam',
          text: 'Tiện nghi thành phố, di chuyển, ăn uống, an toàn, văn hóa địa phương và nhịp sinh hoạt của sinh viên.',
          cover: frontimage,
          photos: [
            { src: frontimage, caption: 'Môi trường campus tại Việt Nam và hỗ trợ khi sinh viên tới nơi' },
            { src: groupimage, caption: 'Cộng đồng sinh viên Ấn Độ và hỗ trợ tư vấn' },
            { src: ogImage, caption: 'Sự kiện trường và tương tác có hướng dẫn' },
          ],
        },
        {
          icon: 'college',
          title: 'Đời sống ở trường',
          text: 'Lớp học, học cùng bạn bè, sự kiện campus, mentoring và kỷ luật học tập.',
          cover: groupimage,
          photos: [
            { src: groupimage, caption: 'Nhóm sinh viên và tương tác trong campus' },
            { src: ogImage, caption: 'Lễ của trường và môi trường học thuật' },
            { src: twitterImage, caption: 'Học tập nhóm và hoạt động sinh viên' },
          ],
        },
        {
          icon: 'medical',
          title: 'Máy móc và phòng lab',
          text: 'Mô phỏng, giải phẫu, thiết bị lâm sàng, phòng lab và học tập thực hành.',
          cover: ogImage,
          photos: [
            { src: ogImage, caption: 'Sự kiện trường y và cơ sở hạ tầng học tập' },
            { src: twitterImage, caption: 'Học thực hành và tiếp xúc y khoa có hướng dẫn' },
            { src: frontimage, caption: 'Cơ sở campus và môi trường học hiện đại' },
          ],
        },
        {
          icon: 'home',
          title: 'Ký túc xá và đời sống sinh viên',
          text: 'Chỗ ở, ăn uống, ngân sách hằng ngày, đồ ăn Ấn Độ và hỗ trợ ổn định ban đầu.',
          cover: twitterImage,
          photos: [
            { src: twitterImage, caption: 'Đời sống sinh viên và môi trường hỗ trợ' },
            { src: groupimage, caption: 'Sự thoải mái từ nhóm bạn và cộng đồng' },
            { src: frontimage, caption: 'Khuôn viên trường và hoạt động di chuyển của sinh viên' },
          ],
        },
        {
          icon: 'hospital',
          title: 'Tiếp xúc lâm sàng',
          text: 'Thăm bệnh viện, tiếp xúc sớm, hiểu về chăm sóc bệnh nhân và kỷ luật nghề nghiệp.',
          cover: frontimage,
          photos: [
            { src: frontimage, caption: 'Kế hoạch campus và tiếp xúc bệnh viện' },
            { src: ogImage, caption: 'Môi trường học y khoa cấp đại học' },
            { src: twitterImage, caption: 'Sinh viên tham gia hoạt động học thuật' },
          ],
        },
      ],
    },
    afterMbbs: {
      eyebrow: 'Sau MBBS tại Việt Nam',
      title: 'Lên kế hoạch cho bằng cấp và lộ trình nghề nghiệp cùng lúc.',
      description: 'Cuộc trao đổi tư vấn đúng đắn phải bao gồm chuẩn bị cấp phép, kế hoạch quay về Ấn Độ, yêu cầu internship và định hướng nghề nghiệp thực tế.',
      items: [
        {
          icon: 'india',
          title: 'Kế hoạch trở về Ấn Độ',
          text: 'Sinh viên cần hiểu lộ trình cấp phép mới nhất tại Ấn Độ, yêu cầu internship và kỳ vọng thi cử trước khi chọn trường.',
        },
        {
          icon: 'target',
          title: 'Tư duy chuẩn bị FMGE hoặc NExT',
          text: 'Kế hoạch học nghiêm túc bắt đầu từ năm đầu. Tư vấn giúp hiểu vì sao kỷ luật lâm sàng và ôn tập đều đặn lại quan trọng.',
        },
        {
          icon: 'globe',
          title: 'PG và cơ hội toàn cầu',
          text: 'Định hướng sau MBBS phụ thuộc vào quy định, kết quả thi, internship, ngôn ngữ và luật cấp phép của từng quốc gia.',
        },
        {
          icon: 'compass',
          title: 'Tư vấn nghề nghiệp thực tế',
          text: 'Không có lời hứa đường tắt. Sinh viên sẽ được nhìn rõ về nỗ lực, mốc thời gian, giấy tờ và trách nhiệm cấp phép.',
        },
      ],
    },
    services: {
      eyebrow: 'Dịch vụ và hỗ trợ',
      title: 'Một đội ngũ cho tư vấn, hồ sơ, visa và hỗ trợ khởi hành.',
      items: [
        { icon: 'profile', title: 'Tư vấn điều kiện miễn phí' },
        { icon: 'shortlist', title: 'Lọc trường tại Việt Nam' },
        { icon: 'wallet', title: 'Giải thích học phí và chi phí sinh hoạt' },
        { icon: 'file', title: 'Hỗ trợ hồ sơ và thư mời nhập học' },
        { icon: 'visa', title: 'Hồ sơ visa và hướng dẫn di chuyển' },
        { icon: 'checklist', title: 'Checklist forex, hành lý và đến nơi' },
        { icon: 'users', title: 'Phối hợp với gia đình' },
        { icon: 'heart', title: 'Hỗ trợ sinh viên sau khi tới nơi' },
      ],
    },
    reviews: {
      eyebrow: 'Đánh giá tư vấn',
      title: 'Hướng dẫn chuyên nghiệp được sinh viên và người hỗ trợ gia đình tin tưởng.',
      description: 'Những trải nghiệm tư vấn thực tế từ sinh viên và người phối hợp gia đình trước khi chọn MBBS tại Việt Nam.',
      badge: 'Đánh giá tư vấn đã xác minh',
      items: [
        {
          name: 'Aryan Singh',
          location: 'Lucknow',
          role: 'Student',
          photo: aryanSingh,
          text: 'Dr Satya Overseas đã giúp tôi hiểu rõ các lựa chọn MBBS tại Việt Nam. Tư vấn rất thực tế, minh bạch và tập trung vào điều kiện, học phí và trường phù hợp.',
        },
        {
          name: 'Chirag Gupta',
          location: 'New Delhi',
          role: 'Student',
          photo: chiragGupta,
          text: 'Đội ngũ đã giải thích từng bước quy trình nhập học và cho tôi cái nhìn thực tế về trường, giấy tờ, visa và tiến độ. Điều đó giúp việc quyết định dễ dàng hơn.',
        },
        {
          name: 'Narayan Shukla',
          location: 'Lucknow',
          role: 'Student',
          photo: narayanShukla,
          text: 'Tôi nhận được hướng dẫn rõ ràng về học phí, ký túc xá, an toàn và toàn bộ quy trình nhập học. Tư vấn rất có trách nhiệm và chuyên nghiệp.',
        },
        {
          name: 'Pankhuri Shukla',
          location: 'Lucknow',
          role: 'Technical professional',
          photo: pankhuriShukla,
          text: 'Tôi đang hỗ trợ hồ sơ nhập học cho em trai và cần câu trả lời rõ ràng, thực tế. Dr Satya Overseas đã giải thích lựa chọn trường, giấy tờ, tiến độ và kế hoạch chi phí rất chuyên nghiệp.',
        },
        {
          name: 'Ritu Kushwaha',
          location: 'New Delhi',
          role: 'Student',
          photo: rituKushwaha,
          text: 'Cố vấn đã trả lời các câu hỏi của tôi về an toàn, chỗ ở, ăn uống, di chuyển và hỗ trợ sinh viên một cách bình tĩnh và đáng tin cậy.',
        },
        {
          name: 'Shaurya Verma',
          location: 'Lucknow',
          role: 'Student',
          photo: shauryaVerma,
          text: 'Điểm tốt nhất là sự tư vấn trung thực. Tôi đã hiểu thực tế về MBBS tại Việt Nam, cách chọn trường, hỗ trợ visa và sự chuẩn bị cần thiết sau khi nhập học.',
        },
      ],
    },
    cta: {
      title: 'Bạn cần shortlist trường cá nhân hóa?',
      text: 'Hãy chia sẻ hồ sơ, cố vấn của chúng tôi sẽ gọi để trao đổi về điều kiện, học phí và hướng dẫn giấy tờ.',
      button: 'Yêu cầu tư vấn',
    },
    faq: {
      eyebrow: 'Chúng tôi hỗ trợ như thế nào?',
      title: 'Những câu hỏi thường gặp trước khi quyết định MBBS tại Việt Nam.',
      items: [
        {
          question: 'MBBS tại Việt Nam có phù hợp với sinh viên Ấn Độ không?',
          answer: 'Có thể là một lựa chọn tốt nếu trường, ngôn ngữ giảng dạy, công nhận, ngân sách và lộ trình cấp phép được xác minh đúng với kỳ nhập học hiện tại.',
        },
        {
          question: 'Nếu trượt NEET thì có thể học MBBS ở nước ngoài không?',
          answer: 'Sinh viên Ấn Độ cần kiểm tra quy định mới nhất của NEET và NMC trước khi chọn bất kỳ chương trình MBBS ở nước ngoài nào. Tư vấn có thể hỗ trợ lên kế hoạch thi lại và kỳ nhập học tiếp theo.',
        },
        {
          question: 'Học phí MBBS tại Việt Nam là bao nhiêu?',
          answer: 'Học phí thay đổi theo từng trường và kỳ nhập học. Tư vấn sẽ bao gồm học phí, ký túc xá, ăn uống, visa, di chuyển, bảo hiểm và chi phí sinh hoạt để gia đình nhìn thấy toàn bộ ngân sách.',
        },
        {
          question: 'Dr Satya Overseas có hỗ trợ giấy tờ không?',
          answer: 'Có. Quy trình tư vấn bao gồm checklist giấy tờ, hỗ trợ hồ sơ, hướng dẫn visa và chuẩn bị khởi hành.',
        },
        {
          question: 'Gia đình có thể trao đổi với cố vấn không?',
          answer: 'Có. Việc trao đổi với gia đình rất quan trọng để làm rõ ngân sách, ký túc xá, an toàn, di chuyển và kế hoạch lâu dài.',
        },
        {
          question: 'Các bạn có cam kết kết quả hay cấp phép không?',
          answer: 'Không. Không cố vấn chuyên nghiệp nào có thể đảm bảo kết quả cấp phép. Sinh viên cần chuẩn bị nghiêm túc theo đúng quy định hiện hành.',
        },
      ],
    },
    documents: {
      eyebrow: 'Checklist giấy tờ',
      title: 'Giữ hồ sơ nhập học sạch và đầy đủ trước khi nộp cho trường.',
      description: 'Yêu cầu cuối cùng có thể thay đổi theo từng trường và kỳ nhập học, nhưng những giấy tờ này giúp chuẩn bị bộ hồ sơ MBBS tại Việt Nam từ sớm.',
      items: [
        { title: 'Bảng điểm lớp 10', text: 'Chuẩn bị bản scan hoặc ảnh rõ ràng của bảng điểm lớp 10 của sinh viên.' },
        { title: 'Bằng tốt nghiệp trung học', text: 'Chuẩn bị bản rõ ràng của bằng tốt nghiệp để xác minh học lực.' },
        { title: 'Bảng điểm lớp 12', text: 'Chuẩn bị bản scan hoặc ảnh rõ ràng của bảng điểm lớp 12 của sinh viên.' },
        { title: 'Giấy chuyển trường (TC)', text: 'Giấy chuyển trường do trường gần nhất cấp.' },
        { title: 'Hộ chiếu sinh viên (3 trang)', text: 'Bản sao hộ chiếu cần thiết cho hồ sơ nhập học và visa.' },
        { title: 'Giấy khai sinh của sinh viên', text: 'Thông tin trên giấy khai sinh cần khớp với hộ chiếu và giấy tờ học tập.' },
        { title: 'Thẻ Aadhar của sinh viên', text: 'Giấy tờ nhận dạng của sinh viên để đối chiếu hồ sơ.' },
        { title: 'Thẻ PAN của sinh viên', text: 'Bản sao thẻ PAN của sinh viên khi cần cho bộ hồ sơ.' },
        { title: 'Ảnh hộ chiếu của sinh viên', text: 'Ảnh hộ chiếu mới nhất với nền sạch.' },
        { title: 'Thẻ Aadhar của bố', text: 'Bản sao giấy tờ nhận dạng của bố cho hồ sơ sinh viên.' },
        { title: 'Thẻ Aadhar của mẹ', text: 'Bản sao giấy tờ nhận dạng của mẹ cho hồ sơ sinh viên.' },
        { title: 'Thẻ PAN của bố', text: 'Bản sao PAN của bố cho hồ sơ tài chính hoặc người bảo trợ.' },
        { title: 'Thẻ PAN của mẹ', text: 'Bản sao PAN của mẹ cho hồ sơ tài chính hoặc người bảo trợ.' },
      ],
    },
    contact: {
      eyebrow: 'Yêu cầu MBBS Việt Nam',
      title: 'Nhận hướng dẫn chính thức cho tuyển sinh MBBS tại Việt Nam.',
      description: 'Chỉ cần gửi thông tin một lần. Cùng một lead sẽ xuất hiện trong admin panel để theo dõi cuộc gọi.',
      trustTitle: 'Bạn sẽ nhận được gì',
      trustItems: [
        ['profile', 'Kiểm tra điều kiện cho MBBS tại Việt Nam'],
        ['shortlist', 'Shortlist trường và trao đổi học phí'],
        ['file', 'Tiến độ nhập học và danh sách giấy tờ'],
        ['visa', 'Hướng dẫn visa, di chuyển và đến nơi'],
      ],
      trustButton: 'Nói chuyện với cố vấn',
    },
    footer: {
      brandTag: 'Hướng dẫn MBBS tại Việt Nam',
      description: 'Tư vấn minh bạch cho sinh viên Ấn Độ dự định học MBBS tại Việt Nam: đánh giá hồ sơ, shortlist trường, nộp hồ sơ, visa, di chuyển và hỗ trợ gia đình.',
      quickLinksTitle: 'Liên kết nhanh',
      quickLinks: [
        ['Vì sao Việt Nam', '#why-vietnam'],
        ['Quy trình nhập học', '#process'],
        ['Checklist giấy tờ', '#documents'],
        ['Thông tin trường', '#colleges'],
        ['Đời sống tại Việt Nam', '#life'],
        ['Đánh giá', '#reviews'],
        ['Đăng ký ngay', '#contact-section'],
      ],
      universitiesTitle: 'Các trường tại Việt Nam',
      contactTitle: 'Liên hệ',
      contactLinks: [
        { icon: 'phone', label: '+91 9452275173', href: 'tel:+919452275173' },
        { icon: 'mail', label: 'drsatyaoverseas@gmail.com', href: 'mailto:drsatyaoverseas@gmail.com' },
        { icon: 'whatsapp', label: 'Tư vấn qua WhatsApp', href: 'https://wa.me/919452275173' },
        { icon: 'map', label: 'Pandit Purwa Rd, Shivaji Puram, Indira Nagar, Lucknow, Uttar Pradesh 226028', href: '#footer-map' },
      ],
      visitEyebrow: 'Đến thăm chúng tôi',
      visitTitle: 'Dr. Satya Overseas & Clinic',
      visitAddress: 'Pandit Purwa Rd, Shivaji Puram, Pandit Purava, Indira Nagar, Lucknow, Uttar Pradesh 226028',
      qrTitle: 'Quét mã để mở website',
      qrText: 'Mở trang tư vấn trên điện thoại',
      bottomNote: 'Công nhận trường, điều kiện, học phí, ngôn ngữ giảng dạy và quy định cấp phép phải được xác minh cho kỳ nhập học hiện tại trước khi quyết định.',
      copyright: 'Bản quyền 2026 Dr Satya Overseas',
    },
    floatingEnquiry: 'Yêu cầu',
  },
};

const getPublicContent = (language) => PUBLIC_CONTENT[language] || PUBLIC_CONTENT.en;
const getSourceLabel = (source, content) => content.form.sourceLabels[source] || source;

const getLocalizedColleges = (content) => COLLEGES.map((college, index) => ({
  ...college,
  ...(content.colleges.items[index] || {}),
  logo: college.logo,
  image: college.image,
  website: college.website,
  points: (content.colleges.items[index]?.points || college.points).map((point, pointIndex) => {
    const defaultPoint = college.points[pointIndex];
    return typeof point === 'string'
      ? { icon: defaultPoint?.icon || 'check', text: point }
      : point;
  }),
}));

const getLocalizedPhotoStacks = (content, cloudinaryStacks) => {
  const stackLookup = {};

  cloudinaryStacks.forEach((stack) => {
    if (stack.slug && Array.isArray(stack.photos) && stack.photos.length) {
      stackLookup[stack.slug] = stack.photos;
    }
  });

  return PHOTO_STACKS.map((baseStack, index) => {
    const stack = content.photoStacks.items[index] || baseStack;
    const fallbackStack = baseStack || {};
    const slugs = [
      stack.slug,
      fallbackStack.slug,
      ...(stack.cloudinaryFolders || []),
      ...(fallbackStack.cloudinaryFolders || []),
    ].filter(Boolean);
    const photos = slugs.reduce((matchedPhotos, slug) => (
      matchedPhotos || stackLookup[slug]
    ), null) || stack.photos || fallbackStack.photos || [];

    return {
      ...fallbackStack,
      ...stack,
      slug: fallbackStack.slug || stack.slug,
      cover: photos[0]?.src || stack.cover || fallbackStack.cover,
      photos,
    };
  });
};

const formatDate = (value) => {
  if (!value) {
    return 'Not set';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not set';
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const toInputDateTime = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, 16);
};

const getLeadUpdatePayload = (lead, form) => {
  const payload = {};
  const nextFollowUpValue = form.nextFollowUpAt || '';
  const currentFollowUpValue = toInputDateTime(lead.nextFollowUpAt);
  const callOutcome = String(form.callOutcome || '').trim();
  const currentCallOutcome = String(lead.callOutcome || '').trim();
  const comment = String(form.comment || '').trim();

  if (form.status && form.status !== (lead.status || 'new')) {
    payload.status = form.status;
  }

  if (form.priority && form.priority !== (lead.priority || 'warm')) {
    payload.priority = form.priority;
  }

  if (callOutcome !== currentCallOutcome) {
    payload.callOutcome = callOutcome;
  }

  if (nextFollowUpValue !== currentFollowUpValue) {
    payload.nextFollowUpAt = nextFollowUpValue;
  }

  if (comment) {
    payload.comment = comment;
  }

  return payload;
};

const getLeadPhone = (lead) => {
  const directPhone = String(lead?.phone || '').trim();

  if (directPhone) {
    return directPhone;
  }

  const messageDigits = String(lead?.message || '').replace(/\D/g, '');
  return messageDigits.length >= 7 && messageDigits.length <= 15 ? messageDigits : '';
};

const getPhoneHref = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits ? `tel:${digits}` : '#';
};

const getLeadQuery = (lead) => {
  const message = String(lead?.message || '').trim();
  const phone = getLeadPhone(lead);
  const messageDigits = message.replace(/\D/g, '');
  const phoneDigits = phone.replace(/\D/g, '');

  if (!message || (phoneDigits && messageDigits === phoneDigits)) {
    return 'No separate query shared.';
  }

  return message;
};

function Icon({ name, className = '' }) {
  const iconClassName = className ? `icon ${className}` : 'icon';

  const icons = {
    book: <><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21.5v-16Z" /><path d="M4 5.5A2.5 2.5 0 0 1 6.5 8H20" /></>,
    building: <><path d="M4 21V7l8-4 8 4v14" /><path d="M9 21v-7h6v7" /><path d="M8 10h1M15 10h1" /></>,
    campus: <><path d="M3 10l9-6 9 6" /><path d="M5 10h14v11H5z" /><path d="M9 21v-6h6v6" /></>,
    check: <path d="M5 12.5 9.5 17 19 7" />,
    checklist: <><path d="M8 6h12M8 12h12M8 18h12" /><path d="m3 6 1 1 2-3M3 12l1 1 2-3M3 18l1 1 2-3" /></>,
    city: <><path d="M3 21h18" /><path d="M5 21V9h6v12M13 21V5h6v16" /><path d="M7 12h2M7 16h2M15 8h2M15 12h2M15 16h2" /></>,
    college: <><path d="M3 9l9-5 9 5-9 5-9-5Z" /><path d="M7 12v5c3 2 7 2 10 0v-5" /></>,
    compass: <><circle cx="12" cy="12" r="9" /><path d="m15 9-2 5-5 2 2-5 5-2Z" /></>,
    download: <><path d="M12 3v11" /><path d="m8 10 4 4 4-4" /><path d="M4 21h16" /></>,
    facebook: <path d="M14 8h3V4h-3c-3 0-5 2-5 5v3H6v4h3v5h4v-5h3l1-4h-4V9c0-.6.4-1 1-1Z" />,
    file: <><path d="M6 3h8l4 4v14H6V3Z" /><path d="M14 3v5h4" /><path d="M8 13h8M8 17h6" /></>,
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></>,
    heart: <path d="M20.5 8.5c0 5-8.5 10.5-8.5 10.5S3.5 13.5 3.5 8.5A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 8.5 2.5Z" />,
    home: <><path d="M3 11 12 4l9 7" /><path d="M5 10v11h14V10" /><path d="M10 21v-6h4v6" /></>,
    hospital: <><path d="M5 21V5h14v16" /><path d="M9 21v-5h6v5M12 8v5M9.5 10.5h5" /></>,
    india: <><circle cx="12" cy="12" r="9" /><path d="M5 9h14M5 15h14M12 11v2" /></>,
    instagram: <><rect x="4" y="4" width="16" height="16" rx="5" /><circle cx="12" cy="12" r="3.5" /><path d="M17 7.2h.01" /></>,
    linkedin: <><path d="M6 10v10M6 6v.01M10 20v-6c0-2.2 1.4-4 3.6-4s3.4 1.5 3.4 4v6" /></>,
    mail: <><path d="M4 6h16v12H4z" /><path d="m4 7 8 6 8-6" /></>,
    map: <><path d="M12 21s7-5.2 7-11a7 7 0 0 0-14 0c0 5.8 7 11 7 11Z" /><circle cx="12" cy="10" r="2.5" /></>,
    medical: <><path d="M12 4v16M4 12h16" /><circle cx="12" cy="12" r="9" /></>,
    phone: <path d="M7 4h4l2 5-2.5 1.5A12 12 0 0 0 13.5 14L15 11.5l5 2v4c0 1.4-1.1 2.5-2.5 2.5A14.5 14.5 0 0 1 3.5 6.5C3.5 5.1 4.6 4 6 4h1Z" />,
    plane: <path d="M21 3 10 14M21 3l-6 18-5-7-7-5 18-6Z" />,
    profile: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    send: <><path d="M21 3 10 14" /><path d="m21 3-6 18-5-7-7-5 18-6Z" /></>,
    shortlist: <><path d="M9 6h11M9 12h11M9 18h11" /><path d="m3 6 1.5 1.5L7 4.5M3 12l1.5 1.5L7 10.5M3 18l1.5 1.5L7 16.5" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
    twitter: <path d="M4 4l7.1 8.6L4.6 20H7l5.2-5.9L17 20h3l-6.6-8 6-8h-2.5l-4.6 5.5L7.8 4H4Z" />,
    users: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 21a6 6 0 0 1 12 0M14 16a5 5 0 0 1 7 5" /></>,
    visa: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 8h8M8 12h5M8 16h8" /></>,
    wallet: <><path d="M4 7h15a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4V7Z" /><path d="M4 7l11-3v3" /><path d="M16 13h5" /></>,
    whatsapp: <><path d="M5 19.5 6.2 16A8 8 0 1 1 9 18.1L5 19.5Z" /><path d="M9 8.8c.5 3 2.2 4.9 5.2 6l1.2-1.5-2-.9-.9.8c-1.2-.6-2.1-1.5-2.7-2.7l.8-.9-.9-2-1.7 1.2Z" /></>,
    youtube: <><path d="M4 8.5C4.3 7 5.3 6.3 6.8 6.1 10.2 5.7 13.8 5.7 17.2 6.1c1.5.2 2.5.9 2.8 2.4.4 2.3.4 4.7 0 7-.3 1.5-1.3 2.2-2.8 2.4-3.4.4-7 .4-10.4 0-1.5-.2-2.5-.9-2.8-2.4-.4-2.3-.4-4.7 0-7Z" /><path d="m10 9 5 3-5 3V9Z" /></>,
  };

  return (
    <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true">
      {icons[name] || icons.check}
    </svg>
  );
}

function DocumentIllustration() {
  return (
    <div className="document-visual" aria-hidden="true">
      <svg className="document-pad" viewBox="0 0 420 420">
        <defs>
          <linearGradient id="paperGradient" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#dfe7f7" />
          </linearGradient>
          <linearGradient id="padGradient" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#214f9e" />
            <stop offset="100%" stopColor="#112d63" />
          </linearGradient>
        </defs>
        <circle className="document-pad__halo document-pad__halo--one" cx="210" cy="214" r="162" />
        <circle className="document-pad__halo document-pad__halo--two" cx="298" cy="108" r="42" />
        <circle className="document-pad__halo document-pad__halo--three" cx="84" cy="304" r="50" />
        <ellipse className="document-pad__shadow" cx="210" cy="360" rx="132" ry="18" />

        <g className="document-pad__stack">
          <path className="document-pad__sheet document-pad__sheet--back" d="M125 70h160l50 50v216H125V70Z" />
          <path className="document-pad__fold" d="M285 70v50h50" />
          <path className="document-pad__sheet document-pad__sheet--mid" d="M100 96h168l44 44v218H100V96Z" />
          <path className="document-pad__fold" d="M268 96v44h44" />
        </g>

        <g className="document-pad__clipboard">
          <rect className="document-pad__board" x="72" y="82" width="256" height="292" rx="24" />
          <rect className="document-pad__paper" x="98" y="112" width="204" height="232" rx="16" />
          <path className="document-pad__clip" d="M161 88h78l12 36H149l12-36Z" />
          <circle className="document-pad__clip-dot" cx="200" cy="88" r="13" />

          {[
            [132, 154],
            [132, 190],
            [132, 226],
            [132, 262],
            [132, 298],
          ].map(([x, y], index) => (
            <g key={y}>
              <rect className="document-pad__box" x={x} y={y} width="18" height="18" rx="4" />
              <path className="document-pad__line" d={`M166 ${y + 5}h88`} />
              <path className="document-pad__line document-pad__line--short" d={`M166 ${y + 18}h64`} />
              <path className={`document-pad__tick document-pad__tick--${index + 1}`} d={`m136 ${y + 8} 6 6 13-17`} />
            </g>
          ))}

          <circle className="document-pad__seal" cx="266" cy="306" r="32" />
          <path className="document-pad__seal-check" d="m250 306 11 11 24-30" />
        </g>
      </svg>
    </div>
  );
}

function LeadCaptureForm({ source, onSuccess, compact = false, title = '', content = PUBLIC_CONTENT.en }) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    serviceInterest: content.form.defaultInterest,
    campaign: '',
    message: '',
  });
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  useEffect(() => {
    setForm((current) => ({
      ...current,
      serviceInterest: DEFAULT_INTEREST_VALUES.includes(current.serviceInterest)
        ? content.form.defaultInterest
        : current.serviceInterest,
    }));
  }, [content.form.defaultInterest]);

  const validate = () => {
    const digits = form.phone.replace(/\D/g, '');

    if (form.name.trim().length < 2) {
      return content.form.validation.invalidName;
    }

    if (digits.length < 10 || digits.length > 15) {
      return content.form.validation.invalidPhone;
    }

    if (form.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)) {
      return content.form.validation.invalidEmail;
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validate();

    if (validationError) {
      setSubmissionStatus(validationError);
      return;
    }

    setIsSubmitting(true);
    setSubmissionStatus('');

    try {
      const response = await fetch(`${API_BASE_URL}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...form,
          phone: form.phone.replace(/\D/g, ''),
          message: form.message.trim() || content.form.fallbackMessage,
          source,
        }),
      });
      await readApiResponse(response);

      setSubmissionStatus(content.form.success);
      setForm({
        name: '',
        phone: '',
        email: '',
        serviceInterest: content.form.defaultInterest,
        campaign: '',
        message: '',
      });

      if (onSuccess) {
        window.setTimeout(onSuccess, 900);
      }
    } catch (error) {
      setSubmissionStatus(error.message || content.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className={compact ? 'lead-form lead-form--compact' : 'lead-form'} onSubmit={handleSubmit}>
      <div className="form-heading">
        <span>{getSourceLabel(source, content)}</span>
        <h2>{title || content.form.quickEligibilityTitle}</h2>
        <p>{content.form.intro}</p>
      </div>

      <div className="form-grid">
        <label>
          {content.form.fields.name}
          <input
            type="text"
            value={form.name}
            onChange={(event) => updateField('name', event.target.value)}
            placeholder={content.form.placeholders.name}
            required
          />
        </label>

        <label>
          {content.form.fields.phone}
          <input
            type="tel"
            value={form.phone}
            inputMode="numeric"
            maxLength={15}
            onChange={(event) => updateField('phone', event.target.value.replace(/\D/g, ''))}
            placeholder={content.form.placeholders.phone}
            required
          />
        </label>
      </div>

      <div className="form-grid">
        <label>
          {content.form.fields.email}
          <input
            type="email"
            value={form.email}
            onChange={(event) => updateField('email', event.target.value)}
            placeholder={content.form.placeholders.email}
          />
        </label>

        <label>
          {content.form.fields.interest}
          <input
            type="text"
            value={form.serviceInterest}
            onChange={(event) => updateField('serviceInterest', event.target.value)}
            placeholder={content.form.placeholders.interest}
          />
        </label>
      </div>

      {!compact && (
        <label>
          {content.form.fields.question}
          <textarea
            rows="4"
            value={form.message}
            onChange={(event) => updateField('message', event.target.value)}
            placeholder={content.form.placeholders.question}
          />
        </label>
      )}

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? content.form.submitting : content.form.submit}
      </button>

      {submissionStatus && <p className="form-status">{submissionStatus}</p>}
      <p className="privacy-text">{content.form.privacy}</p>
    </form>
  );
}

function Header({ openLeadModal, content }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const openModalFromMenu = (source) => {
    openLeadModal(source);
    setIsMenuOpen(false);
  };

  return (
    <header className={isMenuOpen ? 'site-header site-header--open' : 'site-header'}>
      <div className="header-main">
        <div className="brand-lockup">
          <img src={logo} alt="Dr Satya Overseas" />
          <div>
            <strong>Dr Satya Overseas</strong>
            <span>{content.brandSub}</span>
          </div>
        </div>

        <button
          className="menu-toggle"
          type="button"
          aria-expanded={isMenuOpen}
          aria-label="Toggle navigation menu"
          onClick={() => setIsMenuOpen((current) => !current)}
        >
          <span />
          <span />
          <span />
        </button>

        <div className="header-actions">
          <a className="button button-light" href="tel:+919452275173"><Icon name="phone" /> {content.header.callNow}</a>
          <a className="button button-light" href={BROCHURE_PATH} download onClick={() => openModalFromMenu('Download Brochure')}>
            <Icon name="download" /> {content.header.brochure}
          </a>
          <button type="button" onClick={() => openModalFromMenu('Apply Now')}><Icon name="send" /> {content.header.applyNow}</button>
        </div>
      </div>

      <nav className="nav-links" aria-label="Primary navigation">
        <button type="button" onClick={() => scrollTo('why-vietnam')}><Icon name="globe" /> {content.header.whyVietnam}</button>
        <button type="button" onClick={() => scrollTo('colleges')}><Icon name="college" /> {content.header.colleges}</button>
        <button type="button" onClick={() => scrollTo('life')}><Icon name="home" /> {content.header.studentLife}</button>
        <button type="button" onClick={() => scrollTo('reviews')}><Icon name="heart" /> {content.header.reviews}</button>
      </nav>
    </header>
  );
}

function LanguageSwitch({ content, language, onLanguageChange }) {
  const handleLanguageChange = typeof onLanguageChange === 'function'
    ? onLanguageChange
    : () => {};

  return (
    <div className="language-switch" aria-label={content.header.languageLabel}>
      {LANGUAGE_OPTIONS.map((option) => (
        <button
          type="button"
          className={language === option.value ? 'language-switch__option language-switch__option--active' : 'language-switch__option'}
          onClick={() => handleLanguageChange(option.value)}
          key={option.value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Hero({ openLeadModal, content, language, onLanguageChange }) {
  const hero = content.hero;
  return (
    <section className="hero">
      <div className="hero-copy">
        <LanguageSwitch content={content} language={language} onLanguageChange={onLanguageChange} /><br></br>
        <span className="badge">{hero.badge}</span>
        <h1>{hero.title}</h1>
        <p>{hero.description}</p>

        <div className="hero-points">
          {hero.points.map(([icon, title, text]) => (
            <div className="point" key={title}>
              <span aria-hidden="true"><Icon name={icon} /></span>
              <div>
                <strong>{title}</strong>
                <p>{text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="hero-actions">
          <button type="button" onClick={() => openLeadModal('Free Counselling')}><Icon name="phone" /> {hero.primaryCta}</button>
          <a className="button button-light" href={BROCHURE_PATH} download onClick={() => openLeadModal('Download Brochure')}>
            <Icon name="download" /> {hero.secondaryCta}
          </a>
        </div>

        <div className="trust-row">
          {hero.trust.map(([icon, value, label]) => (
            <div key={`${value}-${label}`}><Icon name={icon} /><strong>{value}</strong><span>{label}</span></div>
          ))}
        </div>
      </div>

      <div className="hero-media">
        <img src={frontimage} alt="Vietnam MBBS students with Dr Satya Overseas" />
        <div className="hero-form-panel">
          <LeadCaptureForm source="Hero Eligibility Form" compact content={content} />
        </div>
      </div>
    </section>
  );
}

function WhyVietnamSection({ content }) {
  const section = content.whyVietnam;
  return (
    <section className="section-band" id="why-vietnam">
      <div className="section-heading">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
        <p>{section.description}</p>
      </div>

      <div className="info-grid">
        {section.items.map((item) => (
          <article className="info-card" key={item.title}>
            <Icon name={item.icon} className="card-icon" />
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </div>

      <p className="note">
        {section.note}
      </p>
    </section>
  );
}

function ProcessSection({ content }) {
  const section = content.process;
  return (
    <section className="section-band" id="process">
      <div className="section-heading">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
      </div>

      <div className="step-grid">
        {section.items.map((step, index) => (
          <article className="step-card" key={step.title}>
            <span><Icon name={step.icon} /><small>{index + 1}</small></span>
            <div>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function DocumentsSection({ content }) {
  const section = content.documents;
  return (
    <section className="section-band documents-section" id="documents">
      <div className="documents-card">
        <DocumentIllustration />

        <div className="documents-copy">
          <span className="eyebrow">{section.eyebrow}</span>
          <h2>{section.title}</h2>
          <p>{section.description}</p>

          <div className="document-list">
            {section.items.map((document, index) => (
              <article key={document.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{document.title}</h3>
                  <p>{document.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CollegesSection({ openLeadModal, content }) {
  const section = content.colleges;
  const colleges = getLocalizedColleges(content);
  return (
    <section className="section-band section-muted" id="colleges">
      <div className="section-heading">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
        <p>{section.description}</p>
      </div>

      <div className="college-grid">
        {colleges.map((college) => (
          <article className="college-card" key={college.name}>
            <div className="college-card__content">
              <div className="college-card__meta">
                <Icon name={college.icon} />
                <span>{college.city}</span>
              </div>
              <div className="college-card__title">
                <img src={college.logo} alt={`${college.name} logo`} />
                <h3>{college.name}</h3>
              </div>
              <strong className="college-fee">{college.fee}</strong>
              <p>{college.text}</p>
              <ul>
                {college.points.map((point) => {
                  const item = typeof point === 'string' ? { icon: 'check', text: point } : point;
                  return (
                    <li key={item.text}>
                      <Icon name={item.icon || 'check'} />
                      <span>{item.text}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="college-actions">
                <button type="button" onClick={() => openLeadModal(`${college.name} Enquiry`)}>
                  <Icon name="phone" /> {section.applyNow}
                </button>
                <a className="button button-light" href={college.website} target="_blank" rel="noreferrer">
                  <Icon name="globe" /> Website
                </a>
                <a className="button button-light" href={BROCHURE_PATH} download onClick={() => openLeadModal('Download Brochure')}>
                  <Icon name="download" /> {section.downloadBrochure}
                </a>
              </div>
            </div>
            <img className="college-card__photo" src={college.image} alt={`${college.name} Vietnam MBBS campus and student guidance`} />
          </article>
        ))}
      </div>
    </section>
  );
}

function LifeSection({ content }) {
  const section = content.life;
  return (
    <section className="section-band" id="life">
      <div className="split-section">
        <div>
          <span className="eyebrow">{section.eyebrow}</span>
          <h2>{section.title}</h2>
          <p>{section.description}</p>
        </div>
        <img src={groupimage} alt="Students supported by Dr Satya Overseas" />
      </div>

      <div className="info-grid info-grid--four">
        {section.items.map((item) => (
          <article className="info-card" key={item.title}>
            <Icon name={item.icon} className="card-icon" />
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function PhotoStacksSection({ content }) {
  const [activeStack, setActiveStack] = useState(null);
  const [cloudinaryStacks, setCloudinaryStacks] = useState([]);
  const section = content.photoStacks;
  const stacks = useMemo(
    () => getLocalizedPhotoStacks(content, cloudinaryStacks),
    [content, cloudinaryStacks],
  );

  useEffect(() => {
    let isMounted = true;

    const fetchPhotoStacks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/photos/stacks`);
        const data = await readApiResponse(response);

        if (isMounted && Array.isArray(data.stacks)) {
          setCloudinaryStacks(data.stacks);
        }
      } catch (error) {
        if (isMounted) {
          setCloudinaryStacks([]);
        }
      }
    };

    fetchPhotoStacks();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section className="section-band photo-stack-section">
      <div className="section-heading">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
        <p>{section.description}</p>
      </div>

      <div className="photo-stack-grid">
        {stacks.map((stack) => (
          <button type="button" className="photo-stack-card" key={stack.title} onClick={() => setActiveStack(stack)}>
            <span className="stack-icon"><Icon name={stack.icon} /></span>
            <span className="stack-images" aria-hidden="true">
              {stack.photos.slice(0, 3).map((photo) => (
                <img src={photo.src} alt="" key={photo.caption} />
              ))}
            </span>
            <strong>{stack.title}</strong>
            <small>{stack.text}</small>
          </button>
        ))}
      </div>

      {activeStack && (
        <div className="modal-backdrop" role="presentation">
          <div className="photo-modal" role="dialog" aria-modal="true" aria-labelledby="photo-stack-title">
            <button className="modal-close modal-close--icon" type="button" onClick={() => setActiveStack(null)} aria-label={section.closeAria}>
              <span />
              <span />
            </button>
            <div className="photo-modal__head">
              <Icon name={activeStack.icon} className="card-icon" />
              <div>
                <span className="eyebrow">{section.modalEyebrow}</span>
                <h2 id="photo-stack-title">{activeStack.title}</h2>
                <p>{activeStack.text}</p>
              </div>
            </div>
            <div className="photo-gallery">
              {activeStack.photos.map((photo, index) => (
                <figure key={photo.publicId || photo.src || `${activeStack.title}-${index}`}>
                  <img src={photo.src} alt={`${activeStack.title} ${index + 1}`} />
                </figure>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function AfterMbbsSection({ content }) {
  const section = content.afterMbbs;
  return (
    <section className="section-band section-muted">
      <div className="section-heading">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
        <p>{section.description}</p>
      </div>

      <div className="info-grid info-grid--four">
        {section.items.map((item) => (
          <article className="info-card" key={item.title}>
            <Icon name={item.icon} className="card-icon" />
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function ServicesSection({ content }) {
  const section = content.services;
  return (
    <section className="services-section">
      <div className="section-heading">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
      </div>

      <div className="service-grid">
        {section.items.map((service) => (
          <div key={service.title}>
            <Icon name={service.icon} />
            <span>{service.title}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function CounsellingCta({ openLeadModal, content }) {
  const section = content.cta;
  return (
    <section className="review-cta-section">
      <div className="cta-strip">
        <div>
          <h3>{section.title}</h3>
          <p>{section.text}</p>
        </div>
        <button type="button" onClick={() => openLeadModal('Review CTA')}><Icon name="phone" /> {section.button}</button>
      </div>
    </section>
  );
}

function ReviewsSection({ content }) {
  const section = content.reviews;
  return (
    <section className="section-band" id="reviews">
      <div className="section-heading">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
        <p>{section.description}</p>
      </div>

      <div className="review-grid">
        {REVIEWS.map((review) => (
          <article className="review-card" key={review.name}>
            <div className="review-card__top">
              <img className="review-photo" src={review.photo} alt={`${review.name} review`} />
              <div>
                <strong>{review.name}</strong>
                <span>{review.role} - {review.location}</span>
              </div>
            </div>
            <p>{review.text}</p>
            <div className="review-badge"><Icon name="check" /> {section.badge}</div>
          </article>
        ))}
      </div>
    </section>
  );
}

function FaqSection({ content }) {
  const section = content.faq;
  return (
    <section className="section-band section-muted">
      <div className="section-heading">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
      </div>

      <div className="faq-grid">
        {section.items.map(({ question, answer }) => (
          <article className="faq-card" key={question}>
            <Icon name="checklist" className="card-icon" />
            <h3>{question}</h3>
            <p>{answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function ContactSection({ openLeadModal, content }) {
  const section = content.contact;
  return (
    <section className="contact-section" id="contact-section">
      <div className="section-heading section-heading--center">
        <span className="eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
        <p>{section.description}</p>
      </div>

      <div className="contact-grid">
        <LeadCaptureForm source="Contact Us" title={content.form.contactTitle} content={content} />

        <aside className="trust-box">
          <img src={groupimage} alt="Dr Satya Overseas student group" />
          <h3>{section.trustTitle}</h3>
          <ul>
            {section.trustItems.map(([icon, label]) => (
              <li key={label}><Icon name={icon} /> {label}</li>
            ))}
          </ul>
          <button type="button" onClick={() => openLeadModal('Trust Box CTA')}><Icon name="phone" /> {section.trustButton}</button>
        </aside>
      </div>
    </section>
  );
}

function Footer({ content }) {
  const footer = content.footer;
  const linkGroups = [
    { title: footer.quickLinksTitle, links: footer.quickLinks },
    { title: footer.universitiesTitle, links: COLLEGES.map((college) => [college.name, '#colleges']) },
  ];
  return (
    <footer className="site-footer">
      <div className="footer-main">
        <div className="footer-brand">
          <div className="brand-lockup brand-lockup--footer">
            <img src={logo} alt="Dr Satya Overseas" />
            <div>
              <strong>Dr Satya Overseas</strong>
              <span>{footer.brandTag}</span>
            </div>
          </div>
          <p>
            {footer.description}
          </p>
          <div className="footer-socials" aria-label="Social media links">
            {SOCIAL_LINKS.map((social) => (
              <a href={social.href} key={social.label} aria-label={social.label} target="_blank" rel="noreferrer">
                <Icon name={social.icon} />
              </a>
            ))}
          </div>
        </div>

        {linkGroups.map((group) => (
          <div className="footer-column" key={group.title}>
            <h3>{group.title}</h3>
            <ul>
              {group.links.map(([label, href]) => (
                <li key={label}>
                  <a href={href}>{label}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="footer-column footer-contact">
          <h3>{footer.contactTitle}</h3>
          <ul>
            {footer.contactLinks.map((item) => (
              <li key={item.label}>
                <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel={item.href.startsWith('http') ? 'noreferrer' : undefined}>
                  <Icon name={item.icon} />
                  <span>{item.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="footer-map" id="footer-map">
        <div className="footer-map__text">
          <span className="eyebrow">{footer.visitEyebrow}</span>
          <h3>{footer.visitTitle}</h3>
          <p>{footer.visitAddress}</p>
          <div className="footer-qr">
            <img src={qrCode} alt="Dr Satya Overseas website QR code" />
            <div>
              <strong>{footer.qrTitle}</strong>
              <span>{footer.qrText}</span>
            </div>
          </div>
        </div>
        <iframe
          title="Dr. Satya Overseas and Clinic map location"
          src={GOOGLE_MAP_EMBED_URL}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      <div className="footer-bottom">
        <p>
          {footer.bottomNote}
        </p>
        <span>{footer.copyright}</span>
      </div>
    </footer>
  );
}

function LandingPage({ openLeadModal, content, language, onLanguageChange }) {
  return (
    <div className="site-shell">
      <Header openLeadModal={openLeadModal} content={content} />
      <main>
        <Hero openLeadModal={openLeadModal} content={content} language={language} onLanguageChange={onLanguageChange} />
        <WhyVietnamSection content={content} />
        <ProcessSection content={content} />
        <CollegesSection openLeadModal={openLeadModal} content={content} />
        <LifeSection content={content} />
        <PhotoStacksSection content={content} />
        <AfterMbbsSection content={content} />
        <ServicesSection content={content} />
        <ReviewsSection content={content} />
        <CounsellingCta openLeadModal={openLeadModal} content={content} />
        <FaqSection content={content} />
        <DocumentsSection content={content} />
        <ContactSection openLeadModal={openLeadModal} content={content} />
      </main>
      <Footer content={content} />
      <button className="floating-enquiry" type="button" onClick={() => openLeadModal('Floating Enquiry')}>
        <Icon name="phone" /> {content.floatingEnquiry}
      </button>
    </div>
  );
}

function LoginPanel({ onLogin }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitLogin = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await readApiResponse(response);

      onLogin(data.token);
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-login">
      <form className="login-card" onSubmit={submitLogin}>
        <span className="eyebrow">Admin only</span>
        <h1>Login to lead desk</h1>
        <p>Use server env values for ADMIN_USERNAME and ADMIN_PASSWORD.</p>

        <label>
          Username
          <input
            type="text"
            value={credentials.username}
            onChange={(event) => setCredentials((current) => ({ ...current, username: event.target.value }))}
            required
          />
        </label>

        <label>
          Password
          <input
            type="password"
            value={credentials.password}
            onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))}
            required
          />
        </label>

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Checking...' : 'Login'}
        </button>

        {error && <p className="form-status form-status--error">{error}</p>}
      </form>
    </div>
  );
}

function AdminFilters({ filters, sources, onChange, onReset }) {
  const updateFilter = (field, value) => {
    onChange({
      ...filters,
      [field]: value,
    });
  };

  return (
    <div className="admin-filters">
      <label>
        Search
        <input
          type="search"
          value={filters.q}
          onChange={(event) => updateFilter('q', event.target.value)}
          placeholder="Name, phone, email, query"
        />
      </label>

      <label>
        Status
        <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
          <option value="all">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option value={value} key={value}>{label}</option>
          ))}
        </select>
      </label>

      <label>
        Priority
        <select value={filters.priority} onChange={(event) => updateFilter('priority', event.target.value)}>
          <option value="all">All priorities</option>
          {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
            <option value={value} key={value}>{label}</option>
          ))}
        </select>
      </label>

      <label>
        Source
        <select value={filters.source} onChange={(event) => updateFilter('source', event.target.value)}>
          <option value="all">All sources</option>
          {sources.map((source) => (
            <option value={source} key={source}>{source}</option>
          ))}
        </select>
      </label>

      <label>
        Follow-up
        <select value={filters.followUp} onChange={(event) => updateFilter('followUp', event.target.value)}>
          <option value="all">All</option>
          <option value="due">Due now</option>
        </select>
      </label>

      <label>
        From
        <input type="date" value={filters.dateFrom} onChange={(event) => updateFilter('dateFrom', event.target.value)} />
      </label>

      <label>
        To
        <input type="date" value={filters.dateTo} onChange={(event) => updateFilter('dateTo', event.target.value)} />
      </label>

      <label>
        Sort
        <select value={filters.sort} onChange={(event) => updateFilter('sort', event.target.value)}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="nextFollowUp">Next follow-up</option>
          <option value="priority">Priority</option>
        </select>
      </label>

      <button type="button" className="button-light" onClick={onReset}>Clear filters</button>
    </div>
  );
}

function LeadUpdateForm({ lead, onUpdate, isUpdating }) {
  const [updateForm, setUpdateForm] = useState({
    status: 'new',
    priority: 'warm',
    callOutcome: '',
    nextFollowUpAt: '',
    comment: '',
  });

  useEffect(() => {
    if (!lead) {
      return;
    }

    setUpdateForm({
      status: lead.status || 'new',
      priority: lead.priority || 'warm',
      callOutcome: lead.callOutcome || '',
      nextFollowUpAt: toInputDateTime(lead.nextFollowUpAt),
      comment: '',
    });
  }, [lead]);

  const updateField = (field, value) => {
    setUpdateForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submitUpdate = (event) => {
    event.preventDefault();
    onUpdate(lead._id, getLeadUpdatePayload(lead, updateForm));
  };

  return (
    <div className="lead-update-panel">
      <h3>Add admin update</h3>
      <form className="admin-update" onSubmit={submitUpdate}>
        <label>
          Status
          <select value={updateForm.status} onChange={(event) => updateField('status', event.target.value)}>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>

        <label>
          Priority
          <select value={updateForm.priority} onChange={(event) => updateField('priority', event.target.value)}>
            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>

        <label>
          Call outcome
          <input
            type="text"
            value={updateForm.callOutcome}
            onChange={(event) => updateField('callOutcome', event.target.value)}
            placeholder="Connected, no answer, asked to call later"
          />
        </label>

        <label>
          Next follow-up
          <input
            type="datetime-local"
            value={updateForm.nextFollowUpAt}
            onChange={(event) => updateField('nextFollowUpAt', event.target.value)}
          />
        </label>

        <label>
          Admin comment
          <textarea
            rows="3"
            value={updateForm.comment}
            onChange={(event) => updateField('comment', event.target.value)}
            placeholder="Add call notes or next action"
          />
        </label>

        <button type="submit" disabled={isUpdating}>
          {isUpdating ? 'Updating...' : 'Save update'}
        </button>
      </form>
    </div>
  );
}

function LeadTimeline({ lead }) {
  const timeline = [...(lead.timeline || [])].sort((first, second) => (
    new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
  ));

  return (
    <div className="timeline">
      <h3>Timeline history</h3>
      {timeline.length ? timeline.map((item) => (
        <div className="timeline-item" key={`${item.event}-${item.createdAt}-${item.note}`}>
          <strong>{item.label}</strong>
          <span>{formatDate(item.createdAt)} by {item.createdBy || 'system'}</span>
          {item.note && <p>{item.note}</p>}
        </div>
      )) : <p>No timeline yet.</p>}
    </div>
  );
}

function LeadTable({ leads, expandedLeadId, onToggle, onUpdate, isUpdating }) {
  if (!leads.length) {
    return <div className="empty-state">No leads match these filters yet.</div>;
  }

  return (
    <div className="lead-table">
      <div className="lead-table__head">
        <span>Lead</span>
        <span>Contact</span>
        <span>Status</span>
        <span>Priority</span>
        <span>Follow-up</span>
        <span>Action</span>
      </div>

      {leads.map((lead) => {
        const phone = getLeadPhone(lead);
        const isExpanded = expandedLeadId === lead._id;

        return (
          <article className={isExpanded ? 'lead-entry lead-entry--open' : 'lead-entry'} key={lead._id}>
            <div className="lead-table__row">
              <div className="lead-cell lead-cell--lead">
                <strong>{lead.name}</strong>
                <span>{lead.source || 'Contact Us'} - {formatDate(lead.date || lead.createdAt)}</span>
              </div>

              <div className="lead-cell lead-cell--contact">
                {phone ? (
                  <a href={getPhoneHref(phone)}><Icon name="phone" /> {phone}</a>
                ) : (
                  <span>No phone</span>
                )}
                {lead.email ? (
                  <a href={`mailto:${lead.email}`}><Icon name="mail" /> {lead.email}</a>
                ) : (
                  <span>No email</span>
                )}
              </div>

              <div className="lead-cell">
                <span className={`pill pill--${lead.status || 'new'}`}>{STATUS_LABELS[lead.status] || 'New'}</span>
              </div>

              <div className="lead-cell">
                <span className={`priority priority--${lead.priority || 'warm'}`}>{PRIORITY_LABELS[lead.priority] || 'Warm'}</span>
              </div>

              <div className="lead-cell lead-cell--muted">
                {formatDate(lead.nextFollowUpAt)}
              </div>

              <div className="lead-cell lead-cell--action">
                <button type="button" className="button-light" onClick={() => onToggle(isExpanded ? '' : lead._id)}>
                  {isExpanded ? 'Hide' : 'View'}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="lead-expanded">
                <dl className="lead-meta">
                  <div>
                    <dt>Interest</dt>
                    <dd>{lead.serviceInterest || 'Not shared'}</dd>
                  </div>
                  <div>
                    <dt>Campaign</dt>
                    <dd>{lead.campaign || 'Not shared'}</dd>
                  </div>
                  <div>
                    <dt>Last contacted</dt>
                    <dd>{formatDate(lead.lastContactedAt)}</dd>
                  </div>
                  <div>
                    <dt>Call outcome</dt>
                    <dd>{lead.callOutcome || 'Not updated'}</dd>
                  </div>
                </dl>

                <div className="query-box">
                  <strong>Query</strong>
                  <p>{getLeadQuery(lead)}</p>
                </div>

                <LeadTimeline lead={lead} />
                <LeadUpdateForm lead={lead} onUpdate={onUpdate} isUpdating={isUpdating} />
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function SummaryStrip({ summary }) {
  const counts = useMemo(() => {
    const result = {};
    (summary?.statusCounts || []).forEach((item) => {
      result[item._id || 'new'] = item.count;
    });
    return result;
  }, [summary]);

  return (
    <div className="summary-strip">
      {Object.entries(STATUS_LABELS).map(([status, label]) => (
        <div key={status}>
          <strong>{counts[status] || 0}</strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function AdminPanel({ goHome }) {
  const [token, setToken] = useState(() => localStorage.getItem('adminToken') || '');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [leadData, setLeadData] = useState({
    leads: [],
    summary: { statusCounts: [], sourceCounts: [] },
    filters: { sources: [] },
    pagination: { total: 0 },
  });
  const [expandedLeadId, setExpandedLeadId] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken('');
    setExpandedLeadId('');
  };

  const fetchLeads = useCallback(async () => {
    if (!token) {
      return;
    }

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/leads?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        logout();
        throw new Error('Admin session expired. Please login again.');
      }

      const data = await readApiResponse(response);

      setLeadData(data);
      setExpandedLeadId((current) => (
        data.leads.some((lead) => lead._id === current) ? current : ''
      ));
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setIsLoading(false);
    }
  }, [filters, token]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleLogin = (newToken) => {
    localStorage.setItem('adminToken', newToken);
    setToken(newToken);
  };

  const updateLead = async (leadId, updateForm) => {
    if (!leadId) {
      return;
    }

    if (!Object.keys(updateForm).length) {
      setError('No changes selected. Update the status, priority, follow-up, outcome, or add a comment first.');
      return;
    }

    setIsUpdating(true);
    setError('');

    try {
      const requestUpdate = (url, method) => fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateForm),
      });
      let response = await requestUpdate(`${API_BASE_URL}/admin/leads/${leadId}`, 'PATCH');

      if (response.status === 404 || response.status === 405) {
        response = await requestUpdate(`${API_BASE_URL}/admin/leads/${leadId}/update`, 'POST');
      }

      if (response.status === 401) {
        logout();
        throw new Error('Admin session expired. Please login again.');
      }

      const data = await readApiResponse(response);

      setExpandedLeadId(data.lead._id);
      setLeadData((current) => ({
        ...current,
        leads: current.leads.map((lead) => (lead._id === data.lead._id ? data.lead : lead)),
      }));
      fetchLeads();
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!token) {
    return <LoginPanel onLogin={handleLogin} />;
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div>
          <span className="eyebrow">Admin panel</span>
          <h1>Lead tracking desk</h1>
          <p>Filter landed users, check their status, and keep call notes in one timeline.</p>
        </div>
        <div className="admin-header__actions">
          <button type="button" className="button-light" onClick={goHome}>Website</button>
          <button type="button" onClick={logout}>Logout</button>
        </div>
      </header>

      <SummaryStrip summary={leadData.summary} />

      <AdminFilters
        filters={filters}
        sources={leadData.filters?.sources || []}
        onChange={setFilters}
        onReset={() => setFilters(DEFAULT_FILTERS)}
      />

      {error && <p className="form-status form-status--error">{error}</p>}

      <section className="admin-table-section">
        <div className="admin-list-panel">
          <div className="list-head">
            <strong>{leadData.pagination?.total || 0} leads</strong>
            {isLoading && <span>Loading...</span>}
          </div>
          <LeadTable
            leads={leadData.leads || []}
            expandedLeadId={expandedLeadId}
            onToggle={setExpandedLeadId}
            onUpdate={updateLead}
            isUpdating={isUpdating}
          />
        </div>
      </section>
    </div>
  );
}

function LeadModal({ source, onClose, content }) {
  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="lead-modal-title">
        <button className="modal-close" type="button" onClick={onClose} aria-label={content.modal.closeAria}>{content.modal.close}</button>
        <h2 id="lead-modal-title">{content.modal.title}</h2>
        <p>{content.modal.text}</p>
        <LeadCaptureForm source={source} compact onSuccess={onClose} content={content} />
      </div>
    </div>
  );
}

function App() {
  const [route, setRoute] = useState(window.location.pathname);
  const [modalSource, setModalSource] = useState('');
  const [language, setLanguage] = useState('en');
  const content = getPublicContent(language);

  const updateLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    localStorage.setItem('siteLanguage', nextLanguage);
  };

  useEffect(() => {
    const handlePopState = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (route.startsWith('/admin') || sessionStorage.getItem('leadPopupClosed')) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setModalSource('Popup');
    }, 1600);

    return () => window.clearTimeout(timer);
  }, [route]);

  useEffect(() => {
    const robotsMeta = document.querySelector('meta[name="robots"]');
    const descriptionMeta = document.querySelector('meta[name="description"]');

    if (route.startsWith('/admin')) {
      document.title = 'Admin Login | Dr Satya Overseas';
      robotsMeta?.setAttribute('content', 'noindex, nofollow, noarchive');
      descriptionMeta?.setAttribute('content', 'Private admin login for Dr Satya Overseas lead management.');
      return;
    }

    document.documentElement.lang = language;
    document.title = content.seo.title;
    robotsMeta?.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    descriptionMeta?.setAttribute('content', content.seo.description);

    if (typeof window.gtag === 'function') {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: window.location.pathname + window.location.search,
        page_title: content.seo.title,
      });
    }
  }, [route, language, content.seo.title, content.seo.description]);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setRoute(path);
    window.scrollTo({ top: 0 });
  };

  const openLeadModal = (source) => {
    setModalSource(source);
  };

  const closeLeadModal = () => {
    sessionStorage.setItem('leadPopupClosed', 'true');
    setModalSource('');
  };

  if (route.startsWith('/admin')) {
    return <AdminPanel goHome={() => navigate('/')} />;
  }

  return (
    <>
      <LandingPage openLeadModal={openLeadModal} content={content} language={language} onLanguageChange={updateLanguage} />
      {modalSource && <LeadModal source={modalSource} onClose={closeLeadModal} content={content} />}
    </>
  );
}

export default App;
